import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  Button,
  Divider,
  Paper,
  IconButton,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  MenuItem,
  Select,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import AddIcon from "@mui/icons-material/Add";
import { COLORS, TYPOGRAPHY } from "../../constants/color";
import { Icon } from "../../icons";
import PhotoUploadWithDrawing from "../../components/siteCheck/PhotoUploadWithDrawing";
import PhotoUploadWithDrawingDPR from "../../components/siteCheck/PhotoUploadWithDrawingDPR";
import axios from "axios";
import { useAuth } from "../../context/authContext";
import { url } from "../../constants/url";
import { useParams } from "react-router-dom";
import { format } from "date-fns";
import AppToast from "../../components/AppToast"; // Import AppToast
import { useNavigate } from 'react-router-dom';
import useFormPersistence from "../../hooks/useFormPersistence";

/* ========================= MAIN ========================= */

export default function DPR() {
  const { projectId } = useParams();
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);
  const [dprDate, setDprDate] = useState(new Date());
  const [dprEntries, setDprEntries] = useState({
    workProgress: [],
    materialReceived: [],
    machinery: [],
    qualityControl: [],
    safetyObservations: [],
    visitorLogs: [],
    delaysHindrances: [],
    photographs: [],
  });
  const navigate = useNavigate();
  // Toast state
  const [toast, setToast] = useState({
    open: false,
    type: "success",
    title: "",
    message: "",
  });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // Fetch project data
  useEffect(() => {
    const fetchProject = async () => {
      try {
        setLoading(true);
        setError(null);

        const config = {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          },
        };

        const response = await axios.get(
          `${url}/api/inhouse-projects/fetch-by/${projectId}`,
          config
        );

        if (response.data.project) {
          setProject(response.data.project);
        } else {
          setError("Project not found");
        }
      } catch (err) {
        console.error("Error fetching project:", err);
        setError("Failed to load project data. Please try again.");
      } finally {
        setLoading(false);
      }
    };

    if (projectId && token) {
      fetchProject();
    }
  }, [projectId, token]);

  // Fetch existing DPR data for the selected date
  const fetchDPRData = useCallback(async () => {
    try {
      if (loading) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);
      console.log("🚀 fetchDPRData called...");
      console.log("📅 Current dprDate:", dprDate);
      console.log("🏗️ Current projectId:", projectId);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          projectId,
          reportDate: format(dprDate, "yyyy-MM-dd"),
          limit: 100, // Increase limit to get all entries
          page: 1,

        }
      };

      console.log("📡 Making API call to fetch DPR data...");

      const response = await axios.get(`${url}/api/dpr/fetch`, config);

      console.log("📨 API response received:", response.data);
      console.log("📊 DPR groups in response:", response.data.dprs);
      console.log("📈 Number of DPR groups:", response.data.dprs?.length || 0);



      const groupedData = {
        workProgress: [],
        materialReceived: [],
        machinery: [],
        qualityControl: [],
        safetyObservations: [],
        visitorLogs: [],
        delaysHindrances: [],
        photographs: [],
      };

      if (response.data.dprs && Array.isArray(response.data.dprs)) {
        // Debug each group
        response.data.dprs.forEach((dprGroup, groupIndex) => {
          console.log(`\n=== Group ${groupIndex + 1} ===`);
          console.log("Report Type:", dprGroup.reportType);
          console.log("Entries Count:", dprGroup.entries?.length || 0);

          if (dprGroup.entries && Array.isArray(dprGroup.entries)) {
            dprGroup.entries.forEach((entry, entryIndex) => {
              console.log(`  Entry ${entryIndex + 1}:`, {
                id: entry._id,
                title: entry.title,
                materialDescription: entry.materialDescription,
                hasMaterialDetails: !!entry.materialDetails
              });

              // Process based on report type
              switch (dprGroup.reportType) {
                case "Work Progress & Manpower Deployment":
                  const skilledLabour = entry.workProgressDetails?.skilledLabour || {};
                  const unskilledLabour = entry.workProgressDetails?.unskilledLabour || {};

                  groupedData.workProgress.push({
                    id: entry._id,
                    work: entry.workProgressDetails?.workDescription || "",
                    contractor: entry.workProgressDetails?.contractorName || "",
                    skilled: Object.entries(skilledLabour).map(([type, qty]) => `${type} - ${qty}`),
                    unskilled: Object.entries(unskilledLabour).map(([type, qty]) => `${type} - ${qty}`),
                    qty: entry.workProgressDetails?.estimatedQuantity || 0,
                    unit: entry.workProgressDetails?.unit || "Person",
                    remark: entry.adminRemarks || "-",
                  });
                  break;

                case "Material Received Report":
                  console.log("  Material Details:", entry.materialDetails);
                  console.log("  Full entry data:", entry);
                  const materialEntry = {
                    _id: entry._id, // Include the original _id for updates
                    material: entry.materialDescription || "",
                    supplier: entry.supplierName || "",
                    challan: entry.challanNumber || "",
                    unit: entry.unit || "",
                    opening: entry.materialDetails?.openingBalance || 0,
                    received: entry.materialDetails?.receivedToday || 0,
                    total: entry.materialDetails?.totalReceived || 0,
                    consumed: entry.materialDetails?.consumedToday || 0,
                    closing: entry.materialDetails?.closingBalance || 0,
                    remark: entry.adminRemarks || "-",
                  };
                  // Use _id if available, otherwise just use id: entry._id
                  materialEntry.id = entry._id;
                  console.log("  Processed material entry:", materialEntry);
                  groupedData.materialReceived.push(materialEntry);
                  break;

                case "Machinery Report":
                  groupedData.machinery.push({
                    id: entry._id,
                    equipment: entry.machineryDetails?.equipmentName || "",
                    supplier: entry.machineryDetails?.supplierName || "",
                    startReading: entry.machineryDetails?.startReading || 0,
                    endReading: entry.machineryDetails?.endReading || 0,
                    inTime: entry.machineryDetails?.inTime || "",
                    outTime: entry.machineryDetails?.outTime || "",
                    working: entry.machineryDetails?.workingHours || "",
                    planned: entry.machineryDetails?.plannedQty || 0,
                    actual: entry.machineryDetails?.actualQty || 0,
                    remark: entry.adminRemarks || "-",
                  });
                  break;

                case "Quality Control & Testing":
                  groupedData.qualityControl.push({
                    id: entry._id,
                    description: entry.qualityControlDetails?.testDescription || "",
                    location: entry.qualityControlDetails?.locationElement || "",
                    date: entry.qualityControlDetails?.testDate ?
                      format(new Date(entry.qualityControlDetails.testDate), "dd MMM yyyy") : "",
                    count: entry.qualityControlDetails?.numberOfTests || 0,
                    result: entry.qualityControlDetails?.results || "",
                    status: entry.qualityControlDetails?.status || "",
                    remark: entry.adminRemarks || "-",
                  });
                  break;

                case "Safety Observation":
                  groupedData.safetyObservations.push({
                    id: entry._id,
                    item: entry.safetyObservationDetails?.itemName || "",
                    status: entry.safetyObservationDetails?.status || "",
                    remark: entry.adminRemarks || "-",
                  });
                  break;

                case "Visitor Log":
                  groupedData.visitorLogs.push({
                    id: entry._id,
                    name: entry.visitorLogDetails?.visitorName || "",
                    company: entry.visitorLogDetails?.companyName || "",
                    designation: entry.visitorLogDetails?.designation || "",
                    purpose: entry.visitorLogDetails?.purposeOfVisit || "",
                    instructions: entry.visitorLogDetails?.instructionsDecisions || "",
                    timeIn: entry.visitorLogDetails?.timeIn || "",
                    timeOut: entry.visitorLogDetails?.timeOut || "",
                    remark: entry.adminRemarks || "-",
                  });
                  break;

                case "Delay Hindrances":
                  groupedData.delaysHindrances.push({
                    id: entry._id,
                    description: entry.delayHindrancesDetails?.descriptionOfDelay || "",
                    duration: entry.delayHindrancesDetails?.duration || "",
                    reason: entry.delayHindrancesDetails?.reason || "",
                    remark: entry.adminRemarks || "-",
                  });
                  break;

                case "Photograph":
                  groupedData.photographs.push({
                    id: entry._id,
                    src: entry.photographDetails?.photoPath
                      ? (entry.photographDetails.photoPath.startsWith('http')
                        ? entry.photographDetails.photoPath.replace(/\\/g, '/')
                        : `${url}/${entry.photographDetails.photoPath.replace(/\\/g, '/')}`.replace(/([^:])\/\//g, '$1/'))
                      : "https://via.placeholder.com/300x200?text=Photo+Placeholder",
                    activity: entry.photographDetails?.activityLocation || "",
                    remark: entry.adminRemarks || entry.photographDetails?.remarks || "-",
                    photographDetails: entry.photographDetails || {}
                  });
                  break;
              }
            });
          }
        });
      }

      console.log("📋 Final groupedData before setting state:", groupedData);
      console.log("📊 Material entries count:", groupedData.materialReceived.length);
      console.log("📋 Material entries:", groupedData.materialReceived);

      setDprEntries(groupedData);
      setLoading(false);
      setRefreshing(false);

    } catch (err) {
      console.error("Error fetching DPR data:", err);
      console.error("Error response:", err.response?.data);
      console.error("Error status:", err.response?.status);
      setLoading(false);
      setRefreshing(false);
    }
  }, [projectId, token, dprDate, loading]);

  // Fetch DPR data when date changes
  // In your DPR component, update the useEffect
  useEffect(() => {
    if (projectId && token) {
      console.log("Project ID or token changed, fetching DPR data...");
      fetchDPRData();
    }
  }, [projectId, token, dprDate]); // Add dprDate as dependency

  // Function to fetch yesterday's closing balance for a specific material
  const fetchYesterdayClosingBalance = useCallback(async (materialDescription) => {
    try {
      if (!projectId || !materialDescription) return 0;

      // Calculate yesterday's date
      const yesterday = new Date(dprDate);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayFormatted = format(yesterday, "yyyy-MM-dd");

      console.log("Fetching yesterday's closing balance:", {
        materialDescription,
        yesterdayFormatted,
        projectId
      });

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        params: {
          projectId,
          materialDescription: materialDescription.trim(),
          reportDate: yesterdayFormatted,
        }
      };

      const response = await axios.get(`${url}/api/dpr/last-closing-balance`, config);

      console.log("Yesterday's closing balance response:", response.data);

      if (response.data && response.data.length > 0) {
        const closingBalance = response.data[0].closingBalance || 0;
        console.log("Found closing balance:", closingBalance, "for material:", materialDescription);
        return closingBalance;
      }

      console.log("No closing balance found for material:", materialDescription);
      return 0;
    } catch (err) {
      console.error("Error fetching yesterday's closing balance:", err);
      return 0;
    }
  }, [projectId, token, dprDate]);

  // Function to add new DPR entry to state
  const addDPREntry = useCallback((section, entry) => {
    console.log('🚀 addDPREntry called:', {
      section,
      entryId: entry.id,
      entryUnderscoreId: entry._id,
      entry: entry
    });

    setDprEntries(prev => {
      // Prevent adding if an exact ID already exists
      if (prev[section].some(e => e.id === entry.id)) return prev;

      const newEntries = [...prev[section], entry];
      return {
        ...prev,
        [section]: newEntries
      };
    });
  }, []);

  // Function to update DPR entry
  const updateDPREntry = useCallback((section, updatedEntry, oldId = null) => {
    console.log('🚀 updateDPREntry called:', {
      section,
      updatedEntryId: updatedEntry.id,
      updatedEntryUnderscoreId: updatedEntry._id,
      oldId: oldId,
      updatedEntry: updatedEntry
    });

    setDprEntries(prev => {
      const sectionEntries = prev[section] || [];

      // If we are replacing an oldId with a newId (e.g., prefilled -> real)
      // or just updating an existing one.
      const idToMatch = oldId || updatedEntry.id;

      // CRITICAL: Check if a real entry with the SAME MATERIAL already exists (to prevent duplicates)
      const duplicateMaterialEntry = sectionEntries.find(e =>
        e.material === updatedEntry.material &&
        e.id !== idToMatch &&
        !e.id.toString().startsWith('prefill-')
      );

      if (duplicateMaterialEntry && updatedEntry.id.toString().startsWith('prefill-')) {
        console.log('🛑 SKIPPING update: A real entry for this material already exists.', duplicateMaterialEntry.id);
        // If we're trying to update a prefill but a real one exists, just remove the prefill
        return {
          ...prev,
          [section]: sectionEntries.filter(e => e.id !== idToMatch)
        };
      }

      console.log('🔍 Looking for entry with ID to match:', idToMatch);

      let found = false;
      let newSectionEntries = sectionEntries.map(entry => {
        if (entry.id === idToMatch) {
          console.log('✅ Found and matching entry. Old ID:', entry.id, 'New ID:', updatedEntry.id);
          found = true;
          return updatedEntry;
        }
        return entry;
      });

      // If not found, add as new
      if (!found) {
        console.log('➕ Entry not found by ID, adding as new entry');
        newSectionEntries = [...newSectionEntries, updatedEntry];
      }

      return {
        ...prev,
        [section]: newSectionEntries
      };
    });
  }, []);

  // Function to delete DPR entry
  const deleteDPREntry = useCallback((section, entryId) => {
    setDprEntries(prev => ({
      ...prev,
      [section]: prev[section].filter(entry => entry.id !== entryId)
    }));
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: { xs: 0.5, md: 1 } }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box sx={{ p: 1.2, position: 'relative' }} >
        <HeaderSection
          project={project}
          dprDate={dprDate}
          setDprDate={setDprDate}
          refreshing={refreshing}
        />
        <WorkProgressSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.workProgress}
          onAddEntry={(entry) => addDPREntry('workProgress', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('workProgress', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('workProgress', entryId)}
          refreshData={fetchDPRData}
        />
        <MaterialReceivedSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.materialReceived}
          onAddEntry={(entry) => addDPREntry('materialReceived', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('materialReceived', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('materialReceived', entryId)}
          refreshData={fetchDPRData}
          fetchYesterdayClosingBalance={fetchYesterdayClosingBalance}
        />
        <MachineryReportSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.machinery}
          onAddEntry={(entry) => addDPREntry('machinery', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('machinery', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('machinery', entryId)}
          refreshData={fetchDPRData}
        />
        <QualityControlSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.qualityControl}
          onAddEntry={(entry) => addDPREntry('qualityControl', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('qualityControl', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('qualityControl', entryId)}
          refreshData={fetchDPRData}
        />
        <SafetyObservationsSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.safetyObservations}
          onAddEntry={(entry) => addDPREntry('safetyObservations', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('safetyObservations', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('safetyObservations', entryId)}
          refreshData={fetchDPRData}
        />
        <VisitorLogsSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.visitorLogs}
          onAddEntry={(entry) => addDPREntry('visitorLogs', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('visitorLogs', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('visitorLogs', entryId)}
          refreshData={fetchDPRData}
        />
        <DelaysHindrancesSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.delaysHindrances}
          onAddEntry={(entry) => addDPREntry('delaysHindrances', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('delaysHindrances', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('delaysHindrances', entryId)}
          refreshData={fetchDPRData}
        />
        <PhotographsSection
          projectId={projectId}
          dprDate={dprDate}
          entries={dprEntries.photographs}
          onAddEntry={(entry) => addDPREntry('photographs', entry)}
          onUpdateEntry={(entry, oldId) => updateDPREntry('photographs', entry, oldId)}
          onDeleteEntry={(entryId) => deleteDPREntry('photographs', entryId)}
          refreshData={fetchDPRData}
        />
        <SubmitDPRButton
          projectId={projectId}
          dprDate={dprDate}
          dprEntries={dprEntries}
        />

        {/* Global AppToast */}
        <AppToast
          open={toast.open}
          type={toast.type}
          title={toast.title}
          message={toast.message}
          onClose={handleCloseToast}
        />
      </Box>
    </LocalizationProvider>
  );
}

/* ========================= HEADER ========================= */

function HeaderSection({ project, dprDate, setDprDate, refreshing }) {
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const navigate = useNavigate();
  const { isEngineer } = useAuth();
  const formatDate = (date) => {
    if (!date) return "DD/MM/YYYY";
    return format(date, "dd/MM/yyyy");
  };
  const handleClose = () => {
    navigate(-1); // <-- your edit report route
  };
  return (
    <Box sx={{ mb: 3 }}>
      {/* Row 1 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography sx={{ ...TYPOGRAPHY.H2, color: COLORS.PRIMARY }}>
          Daily Progress Report (DPR)
        </Typography>
        <IconButton size="small" onClick={handleClose}>
          <Icon name="close3" color={COLORS.ERROR} />
        </IconButton>
      </Box>

      {/* Row 2 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Project Name:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project?.projectName || "Hare Krishna Valley"}
          </Typography>
          {refreshing && (
            <CircularProgress size={18} thickness={5} sx={{ color: COLORS.SECONDARY, ml: 1 }} />
          )}
        </Box>

        <Box display="flex" alignItems="center" gap={1}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Date:
          </Typography>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              border: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              borderRadius: 1,
              px: 1.2,
              py: 0.3,
              gap: 0.6,
              fontSize: TYPOGRAPHY.BODY_SMALL.fontSize,
              color: COLORS.PRIMARY,
              minWidth: 110,
              justifyContent: "space-between",
              cursor: "pointer",
              "&:hover": {
                borderColor: COLORS.SECONDARY,
              }
            }}
            onClick={() => setDatePickerOpen(true)}
          >
            {formatDate(dprDate)}
            <Icon name="calendar3" color={COLORS.SECONDARY} />
          </Box>
        </Box>
      </Box>

      {/* Row 3 */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Address:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project?.siteAddress || "Pune, Maharashtra"}
          </Typography>
        </Box>
        <Box />
      </Box>

      {/* Row 4 */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Developer:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project?.developerName || "Vyankatesh Contracts"}
          </Typography>
        </Box>

        <Box display="flex" alignItems="center" gap={0.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
            Engineer:
          </Typography>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY }}>
            {project?.assignedEngineer?.name || "Ashish Hase"}
          </Typography>
        </Box>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          mt: 2,
          height: "1px",
          backgroundColor: COLORS.LIGHT_SECONDARY,
          opacity: 0.7,
        }}
      />

      {/* Date Picker Dialog */}
      <Dialog open={datePickerOpen} onClose={() => setDatePickerOpen(false)}>
        <DialogTitle>Select DPR Date</DialogTitle>
        <DialogContent>
          <DatePicker
            value={dprDate}
            onChange={(newValue) => {
              setDprDate(newValue);
              setDatePickerOpen(false);
            }}
            openTo="day"
            views={['year', 'month', 'day']}
            minDate={isEngineer() ? new Date() : undefined}
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                sx={{ mt: 2, display: 'none' }}
                InputProps={{
                  ...params.InputProps,
                  readOnly: true,
                }}
              />
            )}
            PopperProps={{
              placement: 'bottom-start',
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDatePickerOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/* ========================= WORK PROGRESS ========================= */

function WorkProgressSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-work-progress-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && Object.values(parsed).some(v => v && v !== "0" && (typeof v !== 'object' || Object.values(v).some(vv => vv && vv !== "0")))) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // Calculate totals by summing up all entries
  const calculateTotals = () => {
    let totalSkilledLabour = {};
    let totalUnskilledLabour = {}; // Changed from totalSkilledLabour
    let totalQty = 0;

    entries.forEach(entry => {
      // Sum quantities
      totalQty += Number(entry.qty || 0);

      // Sum skilled labour
      entry.skilled?.forEach(labour => {
        const [type, qty] = labour.split(' - ');
        totalSkilledLabour[type] = (totalSkilledLabour[type] || 0) + Number(qty || 0);
      });

      // Sum unskilled labour - Fixed: should use totalUnskilledLabour
      entry.unskilled?.forEach(labour => {
        const [type, qty] = labour.split(' - ');
        totalUnskilledLabour[type] = (totalUnskilledLabour[type] || 0) + Number(qty || 0); // Fixed variable name
      });
    });

    return {
      totalQty,
      totalSkilled: Object.entries(totalSkilledLabour).map(([type, qty]) => `${type} - ${qty}`),
      totalUnskilled: Object.entries(totalUnskilledLabour).map(([type, qty]) => `${type} - ${qty}`)
    };
  };

  const { totalQty, totalSkilled, totalUnskilled } = calculateTotals();

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry) => {
    onUpdateEntry(updatedEntry);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const getRemarkCellStyles = () => ({
    ...TYPOGRAPHY.BODY_LARGE,
    color: COLORS.SECONDARY,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    display: 'block',
    lineHeight: 1.4,
    flex: 1, // Ensure it fills the cell
  });

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Work Progress & Manpower Deployment
      </Typography>

      {/* TABLE */}
      <Box>
        {/* HEADER ROW */}
        <FlexRow
          sx={{
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            pb: 1,
          }}
        >
          <FlexCell w="40px" sx={{ pl: 1 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Sr.No.
            </Typography>
          </FlexCell>

          <FlexCell flex={2} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Work Description
            </Typography>
          </FlexCell>

          <FlexCell flex={1.5} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Contractor Name
            </Typography>
          </FlexCell>

          <FlexCell flex={1.5} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Skilled Labour
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2} sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Unskilled Labour
            </Typography>
          </FlexCell>

          <FlexCell w="60px" sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Est. QTY
            </Typography>
          </FlexCell>

          <FlexCell w="80px" sx={{ pl: 2 }}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Unit
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2} sx={{ pl: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Remark
              </Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {/* DATA ROWS */}
        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                }}
              >
                <FlexCell w="40px" sx={{ pl: 1 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {i + 1}.
                  </Typography>
                </FlexCell>

                <FlexCell flex={2} sx={{ pl: 2 }}>
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.work || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5} sx={{ pl: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.contractor || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5} sx={{ pl: 2 }}>
                  {row.skilled?.map((s, index) => (
                    <Typography
                      key={index}
                      sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
                    >
                      {s}
                    </Typography>
                  ))}
                </FlexCell>

                <FlexCell flex={1.2} sx={{ pl: 2 }}>
                  {row.unskilled?.map((u, index) => (
                    <Typography
                      key={index}
                      sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}
                    >
                      {u}
                    </Typography>
                  ))}
                </FlexCell>

                <FlexCell w="60px" sx={{ pl: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.qty}
                  </Typography>
                </FlexCell>

                <FlexCell w="80px" sx={{ pl: 2 }}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.unit}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.2} sx={{ pl: 2 }}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.SUCCESS}15`,
                          }
                        }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.ERROR}15`,
                          }
                        }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {/* EDIT FORM - Appears directly below the entry being edited */}
              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <EditWorkProgressForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}

        {/* TOTAL ROW */}
        {entries.length > 0 && (
          <FlexRow
            sx={{
              borderTop: `1px solid ${COLORS.LIGHT_SECONDARY}`,
              py: 1,
              backgroundColor: '#f8f9fa'
            }}
          >
            <FlexCell w="40px" sx={{ pl: 1 }} />
            <FlexCell flex={2} sx={{ pl: 2 }} />
            <FlexCell flex={1.5} sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}>
                Total
              </Typography>
            </FlexCell>

            <FlexCell flex={1.5} sx={{ pl: 2 }}>
              {totalSkilled.map((s, index) => (
                <Typography
                  key={index}
                  sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
                >
                  {s}
                </Typography>
              ))}
            </FlexCell>

            <FlexCell flex={1.2} sx={{ pl: 2 }}>
              {totalUnskilled.map((u, index) => (
                <Typography
                  key={index}
                  sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}
                >
                  {u}
                </Typography>
              ))}
            </FlexCell>

            <FlexCell w="60px" sx={{ pl: 2 }}>
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.PRIMARY, fontWeight: 600 }}>
                {totalQty}
              </Typography>
            </FlexCell>

            <FlexCell w="80px" sx={{ pl: 2 }} />
            <FlexCell flex={1.2} sx={{ pl: 2 }} />
          </FlexRow>
        )}
      </Box>

      {/* ADD BUTTON - Always show if not editing */}
      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddWorkProgressForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

/* ========================= ADD/EDIT WORK PROGRESS FORM ========================= */

function AddWorkProgressForm({ onCancel, projectId, dprDate, onAddEntry, onUpdateEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-work-progress-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      workDescription: entry?.work || "",
      contractorName: entry?.contractor || "",
      skilledLabour: isEditMode && entry?.skilled ?
        Object.fromEntries(entry.skilled.map(s => {
          const [type, qty] = s.split(' - ');
          return [type, qty];
        })) : {
          Carpenter: "0",
          Fitter: "0",
          Mason: "0",
        },
      unskilledLabour: isEditMode && entry?.unskilled ?
        Object.fromEntries(entry.unskilled.map(u => {
          const [type, qty] = u.split(' - ');
          return [type, qty];
        })) : {
          "M/C": "0",
          "F/C": "0",
        },
      estimatedQuantity: entry?.qty || 0,
      unit: entry?.unit || "Person",
      customUnit: isEditMode && entry?.unit && !["Bags", "MT", "Cum", "Nos", "Sqm", "Kg", "Person"].includes(entry?.unit) ? entry?.unit : "",
      remark: entry?.remark || "",
    }
  );

  const [newSkilled, setNewSkilled] = useState({ type: "", qty: "" });
  const [newUnskilled, setNewUnskilled] = useState({ type: "", qty: "" });

  const addNewSkilled = () => {
    if (newSkilled.type.trim() && newSkilled.qty.trim()) {
      setFormData({
        ...formData,
        skilledLabour: {
          ...formData.skilledLabour,
          [newSkilled.type]: newSkilled.qty
        }
      });
      setNewSkilled({ type: "", qty: "" });
    }
  };

  const addNewUnskilled = () => {
    if (newUnskilled.type.trim() && newUnskilled.qty.trim()) {
      setFormData({
        ...formData,
        unskilledLabour: {
          ...formData.unskilledLabour,
          [newUnskilled.type]: newUnskilled.qty
        }
      });
      setNewUnskilled({ type: "", qty: "" });
    }
  };

  const removeSkilled = (type) => {
    const newSkilled = { ...formData.skilledLabour };
    delete newSkilled[type];
    setFormData({ ...formData, skilledLabour: newSkilled });
  };

  const removeUnskilled = (type) => {
    const newUnskilled = { ...formData.unskilledLabour };
    delete newUnskilled[type];
    setFormData({ ...formData, unskilledLabour: newUnskilled });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      // Filter out labour entries with zero or empty values
      const filteredSkilledLabour = {};
      const filteredUnskilledLabour = {};

      Object.entries(formData.skilledLabour).forEach(([type, qty]) => {
        if (qty && qty !== "0" && Number(qty) > 0) {
          filteredSkilledLabour[type] = qty;
        }
      });

      Object.entries(formData.unskilledLabour).forEach(([type, qty]) => {
        if (qty && qty !== "0" && Number(qty) > 0) {
          filteredUnskilledLabour[type] = qty;
        }
      });

      const requestData = {
        projectId,
        reportType: "Work Progress & Manpower Deployment",
        title: `Work Progress - ${formData.workDescription || "Daily Report"}`,
        adminRemarks: formData.remark,
        reportDate: format(dprDate, "yyyy-MM-dd"),
        workProgressDetails: {
          workDescription: formData.workDescription,
          contractorName: formData.contractorName,
          skilledLabour: filteredSkilledLabour,
          unskilledLabour: filteredUnskilledLabour,
          estimatedQuantity: Number(formData.estimatedQuantity) || 0,
          unit: formData.unit === 'Other' ? formData.customUnit : formData.unit,
          remarks: formData.remark,
        },
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
        try {
          const getResponse = await axios.get(
            `${url}/api/dpr/fetch/${entry.id}`,
            config
          );

          if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
            const existingData = getResponse.data.data || getResponse.data.dpr;
            requestData._id = existingData._id;

            // Log the request for debugging
            console.log('Sending update request:', {
              url: `${url}/api/dpr/update-entry/${existingData._id}`,
              data: requestData
            });

            response = await axios.put(
              `${url}/api/dpr/update-entry/${existingData._id}`,
              requestData,
              config
            );

            // Log the response for debugging
            console.log('Update response:', response.data);
          }
        } catch (fetchErr) {
          console.error("Error fetching existing entry:", fetchErr);
          // If fetch fails, try to create a new entry
          response = await axios.post(
            `${url}/api/dpr/add`,
            requestData,
            config
          );
        }
      } else {
        // This is a new entry or prefilled entry
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      // Handle the response
      if (response && (response.status === 200 || response.status === 201)) {
        // Handle both response structures: {dpr: {...}} or {data: {...}}
        const responseData = response.data.dpr || response.data.data || response.data;

        if (!responseData) {
          throw new Error("Invalid response format from server");
        }

        setSuccess(true);

        // Prepare the new entry object
        const newEntry = {
          id: entry?.id || responseData._id || `temp-${Date.now()}`,
          _id: entry?._id || responseData._id,
          work: formData.workDescription,
          contractor: formData.contractorName,
          skilled: Object.entries(formData.skilledLabour).map(([type, qty]) => `${type} - ${qty}`),
          unskilled: Object.entries(formData.unskilledLabour).map(([type, qty]) => `${type} - ${qty}`),
          qty: formData.estimatedQuantity,
          unit: formData.unit === 'Other' ? formData.customUnit : formData.unit,
          remark: formData.remark || "-",
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (isEditMode && onUpdateEntry) {
          // Fallback if onSuccess is not provided but onUpdateEntry is
          onUpdateEntry(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        // Show success message
        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Work progress entry updated successfully!" : "Work progress entry added successfully!"
          });
        }

        // Clear persistence on success
        clearFormData();

        // Refresh data and close form
        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      } else {
        throw new Error(response?.data?.message || "Failed to save entry");
      }
    } catch (err) {
      console.error("Error saving Work Progress DPR:", {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMsg = "Failed to save DPR entry";
      if (err.response) {
        const responseData = err.response.data;
        if (typeof responseData === 'string') {
          errorMsg = responseData;
        } else if (responseData.message) {
          errorMsg = responseData.message;
        } else if (responseData.dpr?.error) {
          errorMsg = responseData.dpr.error;
        } else {
          errorMsg = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMsg = 'No response from server. Please check your connection.';
      } else {
        errorMsg = err.message || errorMsg;
      }

      setError(errorMsg);

      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input": {
      color: COLORS.PRIMARY,
      ...TYPOGRAPHY.BODY_LARGE,
      padding: "10px 12px",
    },
    "& input::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  return (
    <Box sx={{ maxHeight: '80vh', overflowY: 'auto' }}>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 2 }}>
        {isEditMode ? "Edit Entry in Work Progress & Manpower Deployment:" : "Add New Entry in Work Progress & Manpower Deployment:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* Work + Contractor */}
      <Box display="flex" gap={3} mb={3}>
        <Box flex={1.6}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Work Description
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Work Description"
            value={formData.workDescription}
            onChange={(e) => setFormData({ ...formData, workDescription: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Contractor Name
          </Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="Contractor Name"
            value={formData.contractorName}
            onChange={(e) => setFormData({ ...formData, contractorName: e.target.value })}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* Skilled / Unskilled */}
      <Box display="flex" gap={5} mb={3}>
        {/* Skilled */}
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 1 }}>
            Skilled Labour
          </Typography>

          {/* Existing skilled labour entries */}
          {Object.entries(formData.skilledLabour).map(([type, qty]) => (
            <Box key={type} display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <TextField
                size="small"
                placeholder="Labour Type"
                value={type}
                sx={{ ...inputSx, width: 180 }}
                onChange={(e) => {
                  const newSkilled = { ...formData.skilledLabour };
                  delete newSkilled[type];
                  newSkilled[e.target.value] = qty;
                  setFormData({ ...formData, skilledLabour: newSkilled });
                }}
              />

              <TextField
                size="small"
                type="number"
                placeholder="00"
                value={qty}
                sx={{ ...inputSx, width: 60 }}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    skilledLabour: { ...formData.skilledLabour, [type]: e.target.value }
                  });
                }}
              />

              <IconButton
                onClick={() => removeSkilled(type)}
                sx={{ color: COLORS.ERROR, ml: -1 }}
              >
                <Icon name="close2" size={18} />
              </IconButton>
            </Box>
          ))}

          {/* Add new skilled labour */}
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <TextField
              size="small"
              placeholder="New Labour Type"
              value={newSkilled.type}
              sx={{ ...inputSx, width: 180 }}
              onChange={(e) => setNewSkilled({ ...newSkilled, type: e.target.value })}
            />

            <TextField
              size="small"
              type="number"
              placeholder="00"
              value={newSkilled.qty}
              sx={{ ...inputSx, width: 60 }}
              onChange={(e) => setNewSkilled({ ...newSkilled, qty: e.target.value })}
            />

            <Button
              startIcon={<Icon name="plus2" />}
              onClick={addNewSkilled}
              disabled={!newSkilled.type.trim() || !newSkilled.qty.trim()}
              sx={{
                backgroundColor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                height: 44,
                borderRadius: "7px",
                minWidth: 100,
                "&:disabled": {
                  backgroundColor: COLORS.LIGHT_SECONDARY,
                }
              }}
            >
              Add
            </Button>
          </Box>
        </Box>

        {/* Unskilled */}
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 1 }}>
            Unskilled Labour
          </Typography>

          {/* Existing unskilled labour entries */}
          {Object.entries(formData.unskilledLabour).map(([type, qty]) => (
            <Box key={type} display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <TextField
                size="small"
                placeholder="Labour Type"
                value={type}
                sx={{ ...inputSx, width: 180 }}
                onChange={(e) => {
                  const newUnskilled = { ...formData.unskilledLabour };
                  delete newUnskilled[type];
                  newUnskilled[e.target.value] = qty;
                  setFormData({ ...formData, unskilledLabour: newUnskilled });
                }}
              />

              <TextField
                size="small"
                type="number"
                placeholder="00"
                value={qty}
                sx={{ ...inputSx, width: 60 }}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    unskilledLabour: { ...formData.unskilledLabour, [type]: e.target.value }
                  });
                }}
              />

              <IconButton
                onClick={() => removeUnskilled(type)}
                sx={{ color: COLORS.ERROR, ml: -1 }}
              >
                <Icon name="close2" size={18} />
              </IconButton>
            </Box>
          ))}

          {/* Add new unskilled labour */}
          <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
            <TextField
              size="small"
              placeholder="New Labour Type"
              value={newUnskilled.type}
              sx={{ ...inputSx, width: 180 }}
              onChange={(e) => setNewUnskilled({ ...newUnskilled, type: e.target.value })}
            />

            <TextField
              size="small"
              type="number"
              placeholder="00"
              value={newUnskilled.qty}
              sx={{ ...inputSx, width: 60 }}
              onChange={(e) => setNewUnskilled({ ...newUnskilled, qty: e.target.value })}
            />

            <Button
              startIcon={<Icon name="plus2" />}
              onClick={addNewUnskilled}
              disabled={!newUnskilled.type.trim() || !newUnskilled.qty.trim()}
              sx={{
                backgroundColor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                height: 44,
                borderRadius: "7px",
                minWidth: 100,
                "&:disabled": {
                  backgroundColor: COLORS.LIGHT_SECONDARY,
                }
              }}
            >
              Add
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Quantity and Unit */}
      <Box display="flex" gap={3} mb={3}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Estimated Quantity
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="number"
            placeholder="0"
            value={formData.estimatedQuantity}
            onChange={(e) => setFormData({ ...formData, estimatedQuantity: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Unit
          </Typography>
          <Select
            fullWidth
            size="small"
            value={formData.unit}
            onChange={(e) => setFormData({ ...formData, unit: e.target.value, customUnit: e.target.value === 'Other' ? formData.customUnit : '' })}
            sx={inputSx}
          >
            <MenuItem value="Bags">Bags</MenuItem>
            <MenuItem value="MT">MT</MenuItem>
            <MenuItem value="Cum">Cum</MenuItem>
            <MenuItem value="Nos">Nos</MenuItem>
            <MenuItem value="Sqm">Sqm</MenuItem>
            <MenuItem value="Kg">Kg</MenuItem>
            <MenuItem value="Person">Person</MenuItem>
            <MenuItem value="Ltr">Ltr</MenuItem>
            <MenuItem value="Other">Other</MenuItem>
          </Select>
          {formData.unit === 'Other' && (
            <TextField
              fullWidth
              size="small"
              placeholder="Enter custom unit"
              value={formData.customUnit || ''}
              onChange={(e) => setFormData({ ...formData, customUnit: e.target.value })}
              sx={{ ...inputSx, mt: 1 }}
            />
          )}
        </Box>
      </Box>

      {/* Remark */}
      <Box mb={3}>
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
          Remark
        </Typography>
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Add remark here"
          value={formData.remark}
          onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
          sx={{
            ...inputSx,
            "& .MuiOutlinedInput-root": {
              height: "auto",
            },
          }}
        />
      </Box>

      {/* Submit and Cancel buttons */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                workDescription: entry?.work || "",
                contractorName: entry?.contractor || "",
                skilledLabour: { Carpenter: "0", Fitter: "0", Mason: "0" },
                unskilledLabour: { "M/C": "0", "F/C": "0" },
                estimatedQuantity: entry?.qty || 0,
                unit: entry?.unit || "Person",
                customUnit: "",
                remark: entry?.remark || "",
              });
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>

        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            px: 3,
            height: 44,
            borderRadius: "7px",
            "&:hover": {
              backgroundColor: success ? COLORS.PRIMARY : COLORS.PRIMARY,
            },
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Updating..." : "Adding...") : success ? (isEditMode ? "Updated!" : "Added!") : (isEditMode ? "Update Entry" : "Add New Entry")}
        </Button>
      </Box>
    </Box>
  );
}

// Alias for EditWorkProgressForm using the same component
function EditWorkProgressForm(props) {
  return <AddWorkProgressForm {...props} />;
}

/* ========================= MATERIAL REPORT ========================= */

const MATERIAL_MASTER = [
  { name: "Cement (OPC/PPC)", unit: "Bags" },
  { name: "Steel/TMT Bars", unit: "MT" },
  { name: "Coarse Aggregate (20mm)", unit: "Cum" },
  { name: "Coarse Aggregate (10mm)", unit: "Cum" },
  { name: "Fine Aggregate / Sand", unit: "Cum" },
  { name: "Bricks", unit: "Nos" },
  { name: "Blocks", unit: "Nos" },
  { name: "Ready Mix Concrete", unit: "Cum" },
  { name: "Concrete Blocks", unit: "Nos" },
  { name: "Plywood / Shuttering", unit: "Sqm" },
  { name: "Binding Wire", unit: "Kg" },
  { name: "Nails", unit: "Kg" },
  { name: "MS Channels / Angles", unit: "MT" },
  { name: "Tile", unit: "Sqm" },
  { name: "Tile Adhesive", unit: "Kg" },
  { name: "Block Jointing Chemical", unit: "Ltr" },
];

function MaterialReceivedSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData, fetchYesterdayClosingBalance }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-material-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && Object.values(parsed).some(v => v && v !== "0")) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // Prefill yesterday's closing balances only for materials that don't have user data yet
  useEffect(() => {
    console.log("🔄 MaterialReceivedSection: Checking for yesterday's balances...");
    const fetchAllYesterdayBalances = async () => {
      if (!fetchYesterdayClosingBalance || !projectId || !dprDate) return;

      for (const material of MATERIAL_MASTER) {
        try {
          // Check if we already have an entry with user-modified data for this material
          const existingEntry = entries.find(e => e.material === material.name);

          // Only prefill if no entry exists OR if the existing entry is a prefill WITHOUT user data
          const isPrefillWithNoData = existingEntry &&
            existingEntry.id?.toString().startsWith('prefill-') &&
            (Number(existingEntry.received) === 0 || existingEntry.received === "-") &&
            (Number(existingEntry.consumed) === 0 || existingEntry.consumed === "-") &&
            (existingEntry.remark === "-" || !existingEntry.remark);

          if (existingEntry && !isPrefillWithNoData) {
            continue; // Skip if we have a real entry or a prefill with data
          }

          const yesterdayClosingBalance = await fetchYesterdayClosingBalance(material.name);

          // Only update if balance > 0
          if (yesterdayClosingBalance > 0) {
            const updatedEntry = {
              id: `prefill-${material.name}`,
              material: material.name,
              unit: material.unit,
              supplier: "-",
              challan: "-",
              opening: yesterdayClosingBalance,
              received: 0,
              total: yesterdayClosingBalance,
              consumed: 0,
              closing: yesterdayClosingBalance,
              remark: "-"
            };

            console.log(`🔧 Updating prefill for ${material.name} with balance ${yesterdayClosingBalance}`);
            onUpdateEntry(updatedEntry);
          }
        } catch (error) {
          console.error(`❌ Error fetching balance for ${material.name}:`, error);
        }
      }
    };

    fetchAllYesterdayBalances();
  }, [fetchYesterdayClosingBalance, projectId, dprDate, onUpdateEntry]);

  // Combined entries: Real entries from DB + Prefilled placeholders for missing materials
  const allEntries = useMemo(() => {
    console.log('📊 MaterialReceivedSection: Recalculating allEntries...');
    const combined = [...entries];

    // Add prefilled placeholders only for materials that have NO entry at all (real or prefilled)
    MATERIAL_MASTER.forEach(master => {
      const alreadyHasEntry = entries.some(e => e.material === master.name);

      if (!alreadyHasEntry) {
        combined.push({
          id: `prefill-${master.name}`,
          material: master.name,
          unit: master.unit,
          supplier: "-",
          challan: "-",
          opening: 0,
          received: 0,
          total: 0,
          consumed: 0,
          closing: 0,
          remark: "-"
        });
      }
    });

    console.log(`📊 Total display entries: ${combined.length}`);
    return combined;
  }, [entries]);

  const formatQty = (val) => String(val || 0).padStart(2, "0");

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry, oldId) => {
    console.log("🎯 handleEditSuccess called with:", updatedEntry, "oldId:", oldId);
    console.log("🔍 onUpdateEntry function type:", typeof onUpdateEntry);
    console.log("🔍 onUpdateEntry function name:", onUpdateEntry?.name);

    try {
      const result = onUpdateEntry(updatedEntry, oldId);
      console.log("✅ onUpdateEntry executed successfully, result:", result);
    } catch (error) {
      console.error("❌ Error calling onUpdateEntry:", error);
    }

    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const getRemarkCellStyles = () => ({
    ...TYPOGRAPHY.BODY_LARGE,
    color: COLORS.SECONDARY,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    display: 'block',
    lineHeight: 1.4,
    marginTop: '4px',
  });

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      // Only delete if it's not a prefilled entry
      if (!entry.id.startsWith('prefill-')) {
        await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      }

      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Material Received Report
      </Typography>

      {/* TABLE */}
      <Box>
        {/* HEADER */}
        <FlexRow
          sx={{
            borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
            pb: 1,
          }}
        >
          <FlexCell w="50px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Sr
            </Typography>
          </FlexCell>

          <FlexCell flex={2} pl={1}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Material Description
            </Typography>
          </FlexCell>

          <FlexCell flex={1.5}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Supplier Name
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Challan No
            </Typography>
          </FlexCell>

          <FlexCell w="60px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Unit
            </Typography>
          </FlexCell>

          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
              Details
            </Typography>
          </FlexCell>

          <FlexCell flex={1.2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>
                Remark
              </Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {/* DATA ROWS - Show all entries (prefilled + user-added) */}
        {allEntries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          allEntries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow
                sx={{
                  borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}`,
                  py: 1,
                }}
              >
                <FlexCell w="50px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {i + 1}.
                  </Typography>
                </FlexCell>

                <FlexCell flex={2} pl={1}>
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.material || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.5}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.supplier || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={1.2}>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.challan || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell w="60px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.unit || "-"}
                  </Typography>
                </FlexCell>

                <FlexCell flex={2}>
                  {[
                    ["Opening Balance", row.opening],
                    ["Received Today", row.received],
                    ["Total Received", row.total],
                    ["Consumed Today", row.consumed],
                    ["Closing Balance", row.closing],
                  ].map(([label, value], index, arr) => (
                    <Box
                      key={label}
                      sx={{
                        borderBottom:
                          index !== arr.length - 1
                            ? `1px solid ${COLORS.LIGHT_SECONDARY}`
                            : "none",
                        pb: 0.4,
                        mb: 0.4,
                        pr: 2,
                        width: "80%",
                      }}
                    >
                      <Typography
                        sx={{
                          ...TYPOGRAPHY.BODY_LARGE,
                          color: COLORS.SECONDARY,
                          display: "flex",
                          gap: 2.3,
                        }}
                      >
                        <span>{label}</span>
                        <span>{formatQty(value)}</span>
                      </Typography>
                    </Box>
                  ))}
                </FlexCell>

                <FlexCell flex={1.2}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.SUCCESS}15`,
                          }
                        }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      {!row.id.startsWith('prefill-') && (
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteEntry(row)}
                          sx={{
                            padding: 0.5,
                            "&:hover": {
                              backgroundColor: `${COLORS.ERROR}15`,
                            }
                          }}
                        >
                          <Icon name="close2" color={COLORS.ERROR} size={20} />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {/* EDIT FORM - Simplified without background/border */}
              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddMaterialForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    masterMaterials={MATERIAL_MASTER}
                    existingEntries={entries}
                    showToast={showToast}
                    fetchYesterdayClosingBalance={fetchYesterdayClosingBalance}
                    onUpdateEntry={onUpdateEntry}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}

        {/* ADD BUTTON - Always show if not editing */}
        {!editingId && (
          <Box display="flex" justifyContent="flex-end" mt={1.5}>
            <Button
              size="small"
              startIcon={<Icon name="plus2" />}
              onClick={() => setShowAddForm(true)}
              sx={{
                backgroundColor: COLORS.SECONDARY,
                color: "#fff",
                textTransform: "none",
                borderRadius: "7px",
                px: 2,
              }}
            >
              Add New Entry
            </Button>
          </Box>
        )}

        {/* ADD FORM */}
        {showAddForm && !editingId && (
          <Box mt={2}>
            <AddMaterialForm
              onCancel={() => setShowAddForm(false)}
              projectId={projectId}
              dprDate={dprDate}
              onAddEntry={handleAddEntry}
              refreshData={refreshData}
              masterMaterials={MATERIAL_MASTER}
              existingEntries={entries}
              showToast={showToast}
              fetchYesterdayClosingBalance={fetchYesterdayClosingBalance}
              onUpdateEntry={onUpdateEntry}
            />
          </Box>
        )}
      </Box>

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

// function AddMaterialForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, masterMaterials, existingEntries, showToast }) {
//   const { token } = useAuth();
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState(null);
//   const [success, setSuccess] = useState(false);

//   const isEditMode = !!entry;

//   const [formData, setFormData] = useState({
//     materialDescription: entry?.material || "",
//     supplierName: entry?.supplier || "",
//     challanNumber: entry?.challan || "",
//     unit: entry?.unit || "",
//     materialDetails: {
//       openingBalance: entry?.opening || 0,
//       receivedToday: entry?.received || 0,
//       totalReceived: entry?.total || 0,
//       consumedToday: entry?.consumed || 0,
//       closingBalance: entry?.closing || 0,
//     },
//     remark: entry?.remark || "",
//   });

//  const handleSubmit = async () => {
//   try {
//     setLoading(true);
//     setError(null);

//     const config = {
//       headers: {
//         Authorization: token ? `Bearer ${token}` : undefined,
//         "Content-Type": "application/json",
//       },
//     };

//     const requestData = {
//       projectId,
//       reportType: "Material Received Report",
//       title: formData.materialDescription ? `Material - ${formData.materialDescription.substring(0, 10)}${formData.materialDescription.length > 10 ? '...' : ''}` : "Material Report",
//       materialDescription: formData.materialDescription,
//       supplierName: formData.supplierName || "",
//       challanNumber: formData.challanNumber || "",
//       unit: formData.unit || "kg",
//       adminRemarks: formData.remark || "",
//       reportDate: dprDate.toISOString(),
//       materialDetails: {
//         openingBalance: Number(formData.openingBalance) || 0,
//         receivedToday: Number(formData.receivedToday) || 0,
//         totalReceived: Number(formData.totalReceived) || 0,
//         consumedToday: Number(formData.consumedToday) || 0,
//         closingBalance: Number(formData.closingBalance) || 0,
//       }
//     };

//     let response;

//     if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
//       try {
//         const getResponse = await axios.get(
//           `${url}/api/dpr/fetch/${entry.id}`,
//           config
//         );

//         if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
//           const existingData = getResponse.data.data || getResponse.data.dpr;
//           requestData._id = existingData._id;

//           console.log('Sending material update request:', {
//             url: `${url}/api/dpr/update-entry/${existingData._id}`,
//             data: requestData
//           });

//           response = await axios.put(
//             `${url}/api/dpr/update-entry/${existingData._id}`,
//             requestData,
//             config
//           );

//           console.log('Material update response:', response.data);
//         }
//       } catch (fetchErr) {
//         console.error("Error fetching existing material entry:", fetchErr);
//         response = await axios.post(
//           `${url}/api/dpr/add`,
//           requestData,
//           config
//         );
//       }
//     } else {
//       response = await axios.post(
//         `${url}/api/dpr/add`,
//         requestData,
//         config
//       );
//     }

//     if (response && (response.status === 200 || response.status === 201)) {
//       const responseData = response.data.dpr || response.data.data || response.data;

//       if (!responseData) {
//         throw new Error("Invalid response format from server");
//       }

//       setSuccess(true);

//       const newEntry = {
//         id: responseData._id || entry?.id || `temp-${Date.now()}`,
//         material: responseData.materialDescription || formData.materialDescription,
//         supplier: responseData.supplierName || formData.supplierName,
//         challan: responseData.challanNumber || formData.challanNumber,
//         unit: responseData.unit || formData.unit,
//         opening: responseData.materialDetails?.openingBalance || formData.openingBalance,
//         received: responseData.materialDetails?.receivedToday || formData.receivedToday,
//         total: responseData.materialDetails?.totalReceived || formData.totalReceived,
//         consumed: responseData.materialDetails?.consumedToday || formData.consumedToday,
//         closing: responseData.materialDetails?.closingBalance || formData.closingBalance,
//         remark: responseData.adminRemarks || formData.remark,
//       };

//       if (isEditMode && onSuccess) {
//         onSuccess(newEntry);
//       } else if (!isEditMode && onAddEntry) {
//         onAddEntry(newEntry);
//       }

//       if (showToast) {
//         showToast({
//           type: "success",
//           title: "Success",
//           message: isEditMode ? "Material entry updated successfully!" : "Material entry added successfully!"
//         });
//       }

//       setTimeout(() => {
//         if (refreshData) refreshData();
//         if (onCancel) onCancel();
//       }, 1500);
//     } else {
//       throw new Error(response?.data?.message || "Failed to save material entry");
//     }
//   } catch (err) {
//     console.error("Error saving Material DPR:", {
//       error: err,
//       response: err.response?.data,
//       status: err.response?.status,
//     });

//     let errorMsg = "Failed to save material entry";
//     if (err.response) {
//       const responseData = err.response.data;
//       if (typeof responseData === 'string') {
//         errorMsg = responseData;
//       } else if (responseData.message) {
//         errorMsg = responseData.message;
//       } else if (responseData.dpr?.error) {
//         errorMsg = responseData.dpr.error;
//       } else {
//         errorMsg = `Server error: ${err.response.status}`;
//       }
//     } else if (err.request) {
//       errorMsg = 'No response from server. Please check your connection.';
//     } else {
//       errorMsg = err.message || errorMsg;
//     }

//     setError(errorMsg);

//     if (showToast) {
//       showToast({
//         type: "error",
//         title: "Error",
//         message: errorMsg
//       });
//     }
//   } finally {
//     setLoading(false);
//   }
// };
//   const inputSx = {
//     "& .MuiOutlinedInput-root": {
//       height: 44,
//       borderRadius: "7px",
//       "& fieldset": {
//         borderColor: COLORS.LIGHT_SECONDARY,
//       },
//       "&:hover fieldset": {
//         borderColor: COLORS.SECONDARY,
//       },
//       "&.Mui-focused fieldset": {
//         borderColor: COLORS.SECONDARY,
//       },
//     },
//     "& input": {
//       ...TYPOGRAPHY.BODY_LARGE,
//       color: COLORS.PRIMARY,
//       padding: "10px 12px",
//     },
//     "& input::placeholder": {
//       color: COLORS.LIGHT_SECONDARY,
//       opacity: 1,
//     },
//   };

//   return (
//     <Box>
//       <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
//         {isEditMode ? "Edit Entry in Material Received Report:" : "Add New Entry in Material Received Report:"}
//       </Typography>

//       {error && (
//         <Alert severity="error" sx={{ mb: 2 }}>
//           {error}
//         </Alert>
//       )}

//       {success && (
//         <Alert severity="success" sx={{ mb: 2 }}>
//           {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
//         </Alert>
//       )}

//       {/* MATERIAL / SUPPLIER / CHALLAN */}
//       <Box display="flex" gap={3} mb={2}>
//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Material Description
//           </Typography>
//           <TextField 
//             fullWidth 
//             placeholder="Material Description" 
//             value={formData.materialDescription}
//             onChange={(e) => setFormData({...formData, materialDescription: e.target.value})}
//             sx={inputSx} 
//           />
//         </Box>

//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Supplier Name
//           </Typography>
//           <TextField 
//             fullWidth 
//             placeholder="Supplier Name" 
//             value={formData.supplierName}
//             onChange={(e) => setFormData({...formData, supplierName: e.target.value})}
//             sx={inputSx} 
//           />
//         </Box>

//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Challan Number
//           </Typography>
//           <TextField 
//             fullWidth 
//             placeholder="Challan No." 
//             value={formData.challanNumber}
//             onChange={(e) => setFormData({...formData, challanNumber: e.target.value})}
//             sx={inputSx} 
//           />
//         </Box>
//       </Box>

//       {/* UNIT */}
//       <Box width="240px" mb={2}>
//         <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//           Unit
//         </Typography>
//         <TextField 
//           fullWidth 
//           placeholder="Unit" 
//           value={formData.unit}
//           onChange={(e) => setFormData({...formData, unit: e.target.value})}
//           sx={inputSx} 
//         />
//       </Box>

//       {/* DETAILS */}
//       <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 1 }}>
//         Details
//       </Typography>

//       <Box display="flex" gap={2} mb={1.5}>
//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Opening Balance
//           </Typography>
//           <TextField
//             fullWidth
//             type="number"
//             value={formData.materialDetails.openingBalance}
//             onChange={(e) => setFormData({
//               ...formData,
//               materialDetails: {...formData.materialDetails, openingBalance: Number(e.target.value)}
//             })}
//             sx={inputSx}
//           />
//         </Box>

//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Received Today
//           </Typography>
//           <TextField
//             fullWidth
//             type="number"
//             value={formData.materialDetails.receivedToday}
//             onChange={(e) => setFormData({
//               ...formData,
//               materialDetails: {...formData.materialDetails, receivedToday: Number(e.target.value)}
//             })}
//             sx={inputSx}
//           />
//         </Box>

//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Total Received
//           </Typography>
//           <TextField
//             fullWidth
//             type="number"
//             value={formData.materialDetails.totalReceived}
//             onChange={(e) => setFormData({
//               ...formData,
//               materialDetails: {...formData.materialDetails, totalReceived: Number(e.target.value)}
//             })}
//             sx={inputSx}
//           />
//         </Box>
//       </Box>

//       <Box display="flex" gap={2} mb={2}>
//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Consumed Today
//           </Typography>
//           <TextField
//             fullWidth
//             type="number"
//             value={formData.materialDetails.consumedToday}
//             onChange={(e) => setFormData({
//               ...formData,
//               materialDetails: {...formData.materialDetails, consumedToday: Number(e.target.value)}
//             })}
//             sx={inputSx}
//           />
//         </Box>

//         <Box flex={1}>
//           <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//             Closing Balance
//           </Typography>
//           <TextField
//             fullWidth
//             type="number"
//             value={formData.materialDetails.closingBalance}
//             onChange={(e) => setFormData({
//               ...formData,
//               materialDetails: {...formData.materialDetails, closingBalance: Number(e.target.value)}
//             })}
//             sx={inputSx}
//           />
//         </Box>
//       </Box>

//       {/* REMARK */}
//       <Box mb={2} width="50%">
//         <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
//           Remark
//         </Typography>
//         <TextField
//           fullWidth
//           multiline
//           minRows={3}
//           placeholder="Remark Text Goes Here"
//           value={formData.remark}
//           onChange={(e) => setFormData({...formData, remark: e.target.value})}
//           sx={{
//             "& .MuiOutlinedInput-root": {
//               borderRadius: "7px",
//               "& fieldset": {
//                 borderColor: COLORS.LIGHT_SECONDARY,
//               },
//             },
//             "& textarea": {
//               ...TYPOGRAPHY.BODY_LARGE,
//               color: COLORS.PRIMARY,
//             },
//           }}
//         />
//       </Box>

//       {/* ACTION BUTTONS */}
//       <Box display="flex" justifyContent="space-between" alignItems="center">
//         <Button
//           onClick={onCancel}
//           startIcon={<Icon name="close2" />}
//           sx={{
//             color: COLORS.ERROR,
//             textTransform: "none",
//             minWidth: 120,
//             border: `1px solid ${COLORS.ERROR}`,
//             borderRadius: "7px",
//             px: 2,
//             py: 1,
//             "&:hover": {
//               backgroundColor: `${COLORS.ERROR}15`,
//               border: `1px solid ${COLORS.ERROR}`,
//             }
//           }}
//         >
//           Cancel
//         </Button>
//         <Button
//           startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
//           onClick={handleSubmit}
//           disabled={loading || success}
//           sx={{
//             backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
//             color: "#fff",
//             textTransform: "none",
//             px: 2.5,
//             height: 44,
//             borderRadius: "7px",
//             "&:hover": {
//               backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
//             },
//             "&:disabled": {
//               backgroundColor: COLORS.LIGHT_SECONDARY,
//             }
//           }}
//         >
//           {loading ? (isEditMode ? "Saving..." : "Adding...") : success ? (isEditMode ? "Saved!" : "Added!") : (isEditMode ? "Save Changes" : "Add New Entry")}
//         </Button>
//       </Box>
//     </Box>
//   );
// }
function AddMaterialForm({ onCancel, projectId, dprDate, onAddEntry, onUpdateEntry, refreshData, entry, onSuccess, masterMaterials, existingEntries, showToast, fetchYesterdayClosingBalance }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  console.log("🔍 AddMaterialForm mode detection:", {
    hasEntry: !!entry,
    entry: entry,
    isEditMode: isEditMode,
    entryId: entry?.id,
    entryUnderscoreId: entry?._id
  });

  // Fixed: Proper form structure with validation
  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-material-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      materialDescription: entry?.material || "",
      supplierName: entry?.supplier || "",
      challanNumber: entry?.challan || "",
      unit: entry?.unit && !["Bags", "MT", "Cum", "Nos", "Sqm", "Kg"].includes(entry?.unit) ? "Other" : (entry?.unit || ""),
      customUnit: entry?.unit && !["Bags", "MT", "Cum", "Nos", "Sqm", "Kg"].includes(entry?.unit) ? entry?.unit : "",
      openingBalance: entry?.opening || 0,
      receivedToday: entry?.received || 0,
      consumedToday: entry?.consumed || 0,
      remark: entry?.remark || "",
    }
  );

  // Fixed: Calculate derived values
  const [calculatedValues, setCalculatedValues] = useState({
    totalReceived: entry?.total || 0,
    closingBalance: entry?.closing || 0,
  });

  // Recalculate when relevant fields change
  useEffect(() => {
    const opening = Number(formData.openingBalance) || 0;
    const received = Number(formData.receivedToday) || 0;
    const consumed = Number(formData.consumedToday) || 0;

    const totalReceived = opening + received;
    const closingBalance = totalReceived - consumed;

    setCalculatedValues({
      totalReceived: totalReceived >= 0 ? totalReceived : 0,
      closingBalance: closingBalance >= 0 ? closingBalance : 0
    });
  }, [formData.openingBalance, formData.receivedToday, formData.consumedToday]);

  // Fetch yesterday's closing balance when material description changes (both add and edit modes)
  useEffect(() => {
    console.log("=== useEffect TRIGGERED ===");
    console.log("useEffect triggered:", {
      materialDescription: formData.materialDescription,
      isEditMode,
      hasFetchFunction: !!fetchYesterdayClosingBalance,
      currentOpeningBalance: formData.openingBalance,
      entryData: entry
    });

    if (formData.materialDescription && fetchYesterdayClosingBalance) {
      console.log("✅ Conditions met - fetching yesterday's closing balance for:", formData.materialDescription);
      const fetchAndSetOpeningBalance = async () => {
        const yesterdayClosingBalance = await fetchYesterdayClosingBalance(formData.materialDescription);

        console.log("Yesterday's closing balance result:", yesterdayClosingBalance);

        // In edit mode, NEVER auto-update opening balance - user has full control
        // In add mode, only update if opening balance is 0 or empty
        if (isEditMode) {
          console.log("⏭️ Edit mode: Never auto-updating opening balance - user has full control");
        } else {
          // Add mode - only update if opening balance is 0 or empty
          if (!formData.openingBalance || formData.openingBalance === 0) {
            console.log("🔧 Add mode: Updating opening balance from", formData.openingBalance, "to", yesterdayClosingBalance);
            setFormData(prev => ({
              ...prev,
              openingBalance: yesterdayClosingBalance
            }));
          } else {
            console.log("⏭️ Add mode: Not updating opening balance - current value:", formData.openingBalance);
          }
        }
      };

      fetchAndSetOpeningBalance();
    } else {
      console.log("❌ Skipping fetch - conditions not met:", {
        hasMaterialDescription: !!formData.materialDescription,
        hasFetchFunction: !!fetchYesterdayClosingBalance
      });
    }
    console.log("=== useEffect END ===");
  }, [formData.materialDescription, fetchYesterdayClosingBalance]); // Removed isEditMode to prevent unwanted triggers

  // Fixed: Handle form submission
  const handleSubmit = async () => {
    try {
      // Validate required fields
      if (!formData.materialDescription.trim()) {
        setError("Material Description is required");
        if (showToast) {
          showToast({
            type: "error",
            title: "Error",
            message: "Material Description is required"
          });
        }
        return;
      }

      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Material Received Report",
        title: formData.materialDescription
          ? `Material - ${formData.materialDescription.substring(0, 10)}${formData.materialDescription.length > 10 ? '...' : ''}`
          : "Material Report",
        materialDescription: formData.materialDescription,
        supplierName: formData.supplierName || "",
        challanNumber: formData.challanNumber || "",
        unit: formData.unit === 'Other' ? formData.customUnit : formData.unit,
        adminRemarks: formData.remark || "",
        reportDate: format(dprDate, "yyyy-MM-dd"),
        materialDetails: {
          openingBalance: Number(formData.openingBalance) || 0,
          receivedToday: Number(formData.receivedToday) || 0,
          totalReceived: calculatedValues.totalReceived,
          consumedToday: Number(formData.consumedToday) || 0,
          closingBalance: calculatedValues.closingBalance,
        }
      };

      console.log("Sending material request data:", requestData);

      let response;

      if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
        // console.log("🔧 EDIT MODE - Attempting to update entry:", {
        //   entryId: entry.id,
        //   entryIdType: typeof entry.id,
        //   hasUnderscoreId: !!entry._id,
        //   underscoreId: entry._id,
        //   fullEntry: entry,
        //   isEditMode: isEditMode,
        //   conditionCheck: {
        //     hasEntry: !!entry,
        //     hasId: !!entry?.id,
        //     notPrefill: !entry.id.startsWith('prefill-'),
        //     idStartsWithPrefill: entry.id.startsWith('prefill-')
        //   }
        // });

        // Try to use _id if available, otherwise use id
        const entryIdToUse = entry._id || entry.id;
        // console.log("🎯 Using entry ID for update:", entryIdToUse);

        try {
          const getResponse = await axios.get(
            `${url}/api/dpr/fetch/${entryIdToUse}`,
            config
          );

          if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
            const existingData = getResponse.data.data || getResponse.data.dpr;
            requestData._id = existingData._id;

            response = await axios.put(
              `${url}/api/dpr/update-entry/${existingData._id}`,
              requestData,
              config
            );
          } else {
            throw new Error("Existing entry not found");
          }
        } catch (fetchErr) {
          console.error("Error updating existing entry:", fetchErr);
          setError("Failed to update entry. Please try again.");
          if (showToast) {
            showToast({
              type: "error",
              title: "Update Failed",
              message: "Failed to update entry. Please try again."
            });
          }
          setLoading(false);
          return;
        }
      } else {
        console.log("🆕 CREATE MODE - Creating new entry instead of updating:", {
          isEditMode: isEditMode,
          hasEntry: !!entry,
          hasId: !!entry?.id,
          entryId: entry?.id,
          idStartsWithPrefill: entry?.id?.startsWith('prefill-'),
          conditionResults: {
            isEditMode: isEditMode,
            entryExists: !!entry,
            entryIdExists: !!entry?.id,
            notPrefill: entry?.id ? !entry.id.startsWith('prefill-') : 'N/A'
          }
        });

        // For new entries AND prefilled entries being edited for the first time,
        // we call the add API to create a real entry in the database.
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      console.log("Response:", response.data);

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;

        if (!responseData) {
          throw new Error("Invalid response format from server");
        }

        setSuccess(true);

        // Create the entry object for local state
        const newEntry = {
          id: responseData._id, // Crucial: use _id so it's no longer 'prefill-...'
          _id: responseData._id, // Set the database ID
          material: responseData.materialDescription || formData.materialDescription,
          supplier: responseData.supplierName || formData.supplierName,
          challan: responseData.challanNumber || formData.challanNumber,
          unit: formData.unit === 'Other' ? formData.customUnit : (responseData.unit || formData.unit),
          opening: responseData.materialDetails?.openingBalance || formData.openingBalance,
          received: responseData.materialDetails?.receivedToday || formData.receivedToday,
          total: responseData.materialDetails?.totalReceived || calculatedValues.totalReceived,
          consumed: responseData.materialDetails?.consumedToday || formData.consumedToday,
          closing: responseData.materialDetails?.closingBalance || calculatedValues.closingBalance,
          remark: responseData.adminRemarks || formData.remark,
        };

        console.log("📊 Created entry object:", newEntry);
        console.log("🆔 Original entry ID:", entry?.id);
        console.log("🆔 Response _id:", responseData._id);
        console.log("🆔 Final entry ID:", newEntry.id);
        console.log("✏️ Is edit mode:", isEditMode);
        console.log("📞 Calling onSuccess:", !!onSuccess);
        console.log("➕ Calling onAddEntry:", !!onAddEntry);

        if (isEditMode && onSuccess) {
          console.log("🔄 Calling onSuccess with entry:", newEntry, "oldId:", entry?.id);
          console.log("🔍 onSuccess function type:", typeof onSuccess);
          console.log("🔍 onSuccess function name:", onSuccess.name);

          try {
            const result = onSuccess(newEntry, entry?.id);
            console.log("✅ onSuccess executed successfully, result:", result);
          } catch (error) {
            console.error("❌ Error calling onSuccess:", error);
          }
        } else if (!isEditMode && onAddEntry) {
          console.log("➕ Calling onAddEntry with entry:", newEntry);
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Material entry updated successfully!" : "Material entry added successfully!"
          });
        }

        // For new entries, refresh data immediately to show the new entry
        if (!isEditMode && refreshData) {
          console.log("🔄 Refreshing data immediately after adding new entry...");
          console.log("🔍 refreshData function:", refreshData);
          console.log("🔍 refreshData function name:", refreshData.name);
          try {
            const refreshResult = refreshData();
            console.log("✅ refreshData called successfully, result:", refreshResult);
          } catch (error) {
            console.error("❌ Error calling refreshData:", error);
          }
        }

        // Clear persistence on success
        clearFormData();

        setTimeout(() => {
          // Refresh data for both add and edit modes to ensure server state is reflected
          if (refreshData) {
            console.log("🔄 Refreshing data after", isEditMode ? "edit" : "add", "... (2)");
            console.log("🔍 refreshData function:", refreshData);
            console.log("🔍 refreshData function name:", refreshData.name);
            try {
              const refreshResult = refreshData();
              console.log("✅ refreshData called successfully, result:", refreshResult);
            } catch (error) {
              console.error("❌ Error calling refreshData:", error);
            }
          }

          // Debug: Check if the entry was actually updated after a delay
          if (isEditMode) {
            console.log("🔍 DEBUG: Checking if entry was updated after 500ms delay...");
            // This will help us see if the state update actually worked
            setTimeout(() => {
              console.log("🔍 DEBUG: Final state check after 1500ms total delay");
            }, 1000);
          }

          if (onCancel) onCancel();
        }, 1500); // Increased delay to 1500ms for more debugging

      } else {
        throw new Error("Failed to save material entry");
      }
    } catch (err) {
      console.error("Error saving material:", {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMsg = "Failed to save material entry";
      if (err.response) {
        if (err.response.data && typeof err.response.data === 'string') {
          errorMsg = err.response.data;
        } else if (err.response.data?.message) {
          errorMsg = err.response.data.message;
        } else if (err.response.data?.error) {
          errorMsg = err.response.data.error;
        }
      }

      setError(errorMsg);

      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper to handle input changes
  const handleInputChange = (field, value) => {
    console.log("🎯 handleInputChange called:", {
      field,
      value,
      isEditMode,
      entryId: entry?.id,
      isPrefilledEntry: entry?.id?.startsWith('prefill-'),
      currentFormData: formData
    });

    const finalValue = (field === 'openingBalance' || field === 'receivedToday' || field === 'consumedToday')
      ? (value === '' ? '' : value)
      : value;

    setFormData(prev => ({
      ...prev,
      [field]: finalValue
    }));

    console.log("📝 Form data after setFormData:", { ...formData, [field]: value });

    // Disable real-time updates to prevent conflicts with final update
    // Only update on form submission
    if (false && isEditMode && entry && onUpdateEntry) {
      const updatedEntry = {
        ...entry,
        [field === 'openingBalance' ? 'opening' : field]: value,
        // Update calculated values if relevant fields change
        ...(field === 'openingBalance' || field === 'receivedToday' || field === 'consumedToday') && {
          opening: field === 'openingBalance' ? Number(value) || 0 : entry.opening,
          received: field === 'receivedToday' ? Number(value) || 0 : entry.received,
          consumed: field === 'consumedToday' ? Number(value) || 0 : entry.consumed,
          // Recalculate total and closing
          total: (field === 'openingBalance' ? Number(value) || 0 : entry.opening) +
            (field === 'receivedToday' ? Number(value) || 0 : entry.received),
          closing: ((field === 'openingBalance' ? Number(value) || 0 : entry.opening) +
            (field === 'receivedToday' ? Number(value) || 0 : entry.received)) -
            (field === 'consumedToday' ? Number(value) || 0 : entry.consumed)
        }
      };

      console.log("🔄 Real-time updating entry from handleInputChange:", updatedEntry);
      console.log("📞 Calling onUpdateEntry with:", updatedEntry);
      onUpdateEntry(updatedEntry);
    } else {
      console.log("⏭️ Real-time updates disabled - will update on form submission:", {
        isEditMode,
        hasEntry: !!entry,
        hasOnUpdateEntry: !!onUpdateEntry
      });
    }
  };

  // Input styling
  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input": {
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.PRIMARY,
      padding: "10px 12px",
    },
    "& input::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Entry in Material Received Report:" : "Add New Entry in Material Received Report:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* MATERIAL / SUPPLIER / CHALLAN */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Material Description *
          </Typography>
          <TextField
            fullWidth
            placeholder="Material Description"
            value={formData.materialDescription}
            onChange={(e) => handleInputChange('materialDescription', e.target.value)}
            sx={inputSx}
            required
            error={!formData.materialDescription.trim()}
            helperText={!formData.materialDescription.trim() ? "This field is required" : ""}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Supplier Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Supplier Name"
            value={formData.supplierName}
            onChange={(e) => handleInputChange('supplierName', e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Challan Number
          </Typography>
          <TextField
            fullWidth
            placeholder="Challan No."
            value={formData.challanNumber}
            onChange={(e) => handleInputChange('challanNumber', e.target.value)}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* UNIT */}
      <Box width="240px" mb={2}>
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
          Unit
        </Typography>
        <Select
          fullWidth
          size="small"
          value={formData.unit}
          onChange={(e) => setFormData({ ...formData, unit: e.target.value, customUnit: e.target.value === 'Other' ? formData.customUnit : '' })}
          sx={inputSx}
          MenuProps={{
            PaperProps: {
              sx: {
                maxHeight: 250, // Fixed height for dropdown
                borderRadius: '7px',
              }
            }
          }}
        >
          <MenuItem value="Bags">Bags</MenuItem>
          <MenuItem value="MT">MT</MenuItem>
          <MenuItem value="Cum">Cum</MenuItem>
          <MenuItem value="Nos">Nos</MenuItem>
          <MenuItem value="Sqm">Sqm</MenuItem>
          <MenuItem value="Kg">Kg</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </Select>
        {formData.unit === 'Other' && (
          <TextField
            fullWidth
            size="small"
            placeholder="Enter custom unit"
            value={formData.customUnit || ''}
            onChange={(e) => setFormData({ ...formData, customUnit: e.target.value })}
            sx={{ ...inputSx, mt: 1 }}
          />
        )}
      </Box>

      {/* DETAILS */}
      <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 1 }}>
        Details
      </Typography>

      <Box display="flex" gap={2} mb={1.5}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Opening Balance
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={formData.openingBalance}
            onChange={(e) => handleInputChange('openingBalance', e.target.value)}
            sx={inputSx}
            InputProps={{
              inputProps: {
                min: 0,
                step: "any"
              }
            }}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Received Today
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={formData.receivedToday}
            onChange={(e) => handleInputChange('receivedToday', e.target.value)}
            sx={inputSx}
            InputProps={{
              inputProps: {
                min: 0,
                step: "any"
              }
            }}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Total Received (Auto-calculated)
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={calculatedValues.totalReceived}
            sx={inputSx}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>
      </Box>

      <Box display="flex" gap={2} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Consumed Today
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={formData.consumedToday}
            onChange={(e) => handleInputChange('consumedToday', e.target.value)}
            sx={inputSx}
            InputProps={{
              inputProps: {
                min: 0,
                step: "any"
              }
            }}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Closing Balance (Auto-calculated)
          </Typography>
          <TextField
            fullWidth
            type="number"
            value={calculatedValues.closingBalance}
            sx={inputSx}
            InputProps={{
              readOnly: true,
            }}
          />
        </Box>
      </Box>

      {/* REMARK */}
      <Box mb={2} width="50%">
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
          Remark
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="Remark Text Goes Here"
          value={formData.remark}
          onChange={(e) => handleInputChange('remark', e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "7px",
              "& fieldset": {
                borderColor: COLORS.LIGHT_SECONDARY,
              },
            },
            "& textarea": {
              ...TYPOGRAPHY.BODY_LARGE,
              color: COLORS.PRIMARY,
            },
          }}
        />
      </Box>

      {/* ACTION BUTTONS */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                materialDescription: entry?.material || "",
                supplierName: entry?.supplier || "",
                challanNumber: entry?.challan || "",
                unit: entry?.unit || "",
                customUnit: "",
                openingBalance: entry?.opening || 0,
                receivedToday: 0,
                consumedToday: 0,
                remark: entry?.remark || "",
              });
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success || !formData.materialDescription.trim()}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            px: 2.5,
            height: 44,
            borderRadius: "7px",
            "&:hover": {
              backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            },
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Saving..." : "Adding...") :
            success ? (isEditMode ? "Saved!" : "Added!") :
              (isEditMode ? "Save Changes" : "Add New Entry")}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= MACHINERY REPORT ========================= */

const EQUIPMENT_MASTER = [
  "Excavator",
  "JCB",
  "Concrete Mixer",
  "Tower Crane",
  "Mobile Crane",
  "Concrete Pumping",
  "Vibrator",
  "Compactor",
  "Bar Bending Machine",
  "Bar Cutting Machine",
  "Welding Machine",
  "Generator / DG Set",
  "RMC Transit Mixer",
  "Concrete Pump (Static)",
  "Hydra Crane",
  "Wheel Loader",
  "Roller",
  "Total Station",
  "Auto Level",
  "Tractor",
  "Water Tanker",
  "Other"
];

function MachineryReportSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-machinery-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && Object.values(parsed).some(v => v && v !== "0")) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  // Create combined entries: first show prefilled equipment, then user-added entries
  const allEntries = [...entries];

  // Check which equipment are not yet in entries
  const missingEquipment = EQUIPMENT_MASTER.filter(equipment =>
    !entries.some(entry => entry.equipment === equipment)
  );

  // Add prefilled entries for missing equipment
  missingEquipment.forEach(equipment => {
    allEntries.push({
      id: `prefill-${equipment}`,
      equipment: equipment,
      supplier: "-",
      startReading: 0,
      endReading: 0,
      inTime: "-",
      outTime: "-",
      working: "-",
      planned: 0,
      actual: 0,
      remark: "-"
    });
  });

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry) => {
    onUpdateEntry(updatedEntry);
    setEditingId(null);
  };

  const getRemarkCellStyles = () => ({
    ...TYPOGRAPHY.BODY_LARGE,
    color: COLORS.SECONDARY,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    display: 'block',
    lineHeight: 1.4,
    marginTop: '4px',
  });

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      // Only delete if it's not a prefilled entry
      if (!entry.id.startsWith('prefill-')) {
        await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      }

      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Machinery Report
      </Typography>

      {/* HEADER */}
      <Box
        display="flex"
        alignItems="center"
        pb={1}
        sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}
      >
        <Box width="40px"><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr.</Typography></Box>
        <Box flex={1.5}><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Equipment Name</Typography></Box>
        <Box flex={1.5}><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Supplier Name</Typography></Box>
        <Box flex={2}><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time</Typography></Box>
        <Box width="90px"><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Planned Qty</Typography></Box>
        <Box width="90px"><Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Actual Qty</Typography></Box>
        <Box flex={1.4}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
          </Box>
        </Box>
      </Box>

      {/* ROWS */}
      {allEntries.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
            No entries yet
          </Typography>
        </Box>
      ) : (
        allEntries.map((row, index) => (
          <React.Fragment key={row.id || index}>
            <Box
              display="flex"
              alignItems="flex-start"
              py={1.2}
              sx={{ borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}
            >
              <Box width="40px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{index + 1}.</Typography>
              </Box>

              <Box flex={1.5}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.equipment || "-"}</Typography>
              </Box>

              <Box flex={1.5}>
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.supplier || "-"}</Typography>
              </Box>

              <Box flex={2}>
                {/* Display Logic based on available data */}
                {(Number(row.startReading) > 0 || Number(row.endReading) > 0) && ((row.inTime !== "" && row.inTime !== "-") || (row.outTime !== "" && row.outTime !== "-")) ? (
                  // Show all values when both readings and time are present
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Start Reading: <span style={{ color: COLORS.PRIMARY }}>{row.startReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      End Reading: <span style={{ color: COLORS.PRIMARY }}>{row.endReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      In Time: <span style={{ color: COLORS.PRIMARY }}>{row.inTime}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Out Time: <span style={{ color: COLORS.PRIMARY }}>{row.outTime}</span>
                    </Typography>
                  </>
                ) : (Number(row.startReading) > 0 || Number(row.endReading) > 0) ? (
                  // Show only readings when only readings are present
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Start Reading: <span style={{ color: COLORS.PRIMARY }}>{row.startReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      End Reading: <span style={{ color: COLORS.PRIMARY }}>{row.endReading || "0"}</span>
                    </Typography>
                  </>
                ) : ((row.inTime !== "" && row.inTime !== "-") || (row.outTime !== "" && row.outTime !== "-")) ? (
                  // Show only in/out time when only time is present
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      In Time: <span style={{ color: COLORS.PRIMARY }}>{row.inTime}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Out Time: <span style={{ color: COLORS.PRIMARY }}>{row.outTime}</span>
                    </Typography>
                  </>
                ) : (
                  // Show all fields with placeholders when nothing has values
                  <>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Start Reading: <span style={{ color: COLORS.PRIMARY }}>{row.startReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      End Reading: <span style={{ color: COLORS.PRIMARY }}>{row.endReading || "0"}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      In Time: <span style={{ color: COLORS.PRIMARY }}>{(row.inTime === "" || row.inTime === "-") ? "-" : row.inTime}</span>
                    </Typography>
                    <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                      Out Time: <span style={{ color: COLORS.PRIMARY }}>{(row.outTime === "" || row.outTime === "-") ? "-" : row.outTime}</span>
                    </Typography>
                  </>
                )}
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                  Working: <span style={{ color: COLORS.PRIMARY }}>{row.working || "-"}</span>
                </Typography>
              </Box>

              <Box width="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{String(row.planned || 0).padStart(2, "0")}</Typography>
              </Box>

              <Box width="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{String(row.actual || 0).padStart(2, "0")}</Typography>
              </Box>

              <Box flex={1.4}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography sx={getRemarkCellStyles()}>
                    {row.remark}
                  </Typography>
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditEntry(row)}
                      sx={{
                        padding: 0.5,
                        "&:hover": {
                          backgroundColor: `${COLORS.SUCCESS}15`,
                        }
                      }}
                    >
                      <Icon name="Action3" color="#8ED081" size={20} />
                    </IconButton>
                    {!row.id.startsWith('prefill-') && (
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.ERROR}15`,
                          }
                        }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>

            {/* EDIT FORM - Simplified without background/border */}
            {editingId === row.id && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <AddMachineryForm
                  entry={row}
                  onCancel={handleCancelEdit}
                  projectId={projectId}
                  dprDate={dprDate}
                  onSuccess={handleEditSuccess}
                  refreshData={refreshData}
                  equipmentMaster={EQUIPMENT_MASTER}
                  existingEntries={entries}
                  showToast={showToast}
                />
              </Box>
            )}
          </React.Fragment>
        ))
      )}

      {/* ADD BUTTON - Always show if not editing */}
      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddMachineryForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            equipmentMaster={EQUIPMENT_MASTER}
            existingEntries={entries}
            showToast={showToast}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddMachineryForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, equipmentMaster, existingEntries, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [timeIn, setTimeIn] = useState(null);
  const [timeOut, setTimeOut] = useState(null);
  const [workingHours, setWorkingHours] = useState(entry?.working || "");

  const isEditMode = !!entry;

  // Parse time strings to Date objects for edit mode
  useEffect(() => {
    if (isEditMode && entry?.inTime && entry.inTime !== "-") {
      const timeString = entry.inTime;
      const [time, modifier] = timeString.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }

      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
      setTimeIn(date);
    }

    if (isEditMode && entry?.outTime && entry.outTime !== "-") {
      const timeString = entry.outTime;
      const [time, modifier] = timeString.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }

      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
      setTimeOut(date);
    }
  }, [isEditMode, entry]);

  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-machinery-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      equipmentName: entry?.equipment || "",
      supplierName: entry?.supplier || "",
      startReading: entry?.startReading || 0,
      endReading: entry?.endReading || 0,
      plannedQty: entry?.planned || 0,
      actualQty: entry?.actual || 0,
      remark: entry?.remark || "",
    }
  );

  // Calculate working hours based on priority
  useEffect(() => {
    const updateWorkingHours = async () => {
      // Priority 1: Reading based calculation (Backend API)
      if (Number(formData.endReading) > 0 || Number(formData.startReading) > 0) {
        try {
          const config = {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              "Content-Type": "application/json",
            },
          };
          const response = await axios.post(`${url}/api/timeDuration/calculate-reading`, {
            startReading: formData.startReading,
            endReading: formData.endReading
          }, config);

          if (response.data.success) {
            setWorkingHours(response.data.workingHours);
          }
        } catch (err) {
          console.error("Error calling calculate-reading API:", err);
        }
        return;
      }

      // Priority 2: Time based calculation (Frontend logic)
      if (timeIn && timeOut) {
        const diff = timeOut.getTime() - timeIn.getTime();
        const hours = Math.floor(diff / (1000 * 60 * 60));
        const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setWorkingHours(`${hours} hrs ${minutes} mins`);
        return;
      }

      setWorkingHours(entry?.working || "");
    };

    updateWorkingHours();
  }, [formData.startReading, formData.endReading, timeIn, timeOut, token, url]);

  const calculateWorkingHoursSync = () => {
    return workingHours;
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const workingHoursValue = workingHours;

      // FIX: Get equipment name properly for prefilled entries
      let equipmentName = formData.equipmentName;

      // If editing a prefilled entry and equipmentName is empty, get it from entry
      if (isEditMode && entry?.id?.startsWith('prefill-') && !equipmentName) {
        equipmentName = entry.equipment || entry.id.replace('prefill-', '');
      }

      // If still empty, use a default
      if (!equipmentName) {
        equipmentName = "Equipment";
      }

      const requestData = {
        projectId,
        reportType: "Machinery Report",
        title: `Machinery - ${equipmentName.substring(0, 30)}`, // Use proper equipment name
        adminRemarks: formData.remark || "",
        reportDate: format(dprDate, "yyyy-MM-dd"),
        machineryDetails: {
          equipmentName: equipmentName, // Use proper equipment name
          supplierName: formData.supplierName || "",
          startReading: Number(formData.startReading) || 0,
          endReading: Number(formData.endReading) || 0,
          inTime: timeIn ? format(timeIn, "hh:mm a") : "",
          outTime: timeOut ? format(timeOut, "hh:mm a") : "",
          workingHours: workingHoursValue,
          plannedQty: Number(formData.plannedQty) || 0,
          actualQty: Number(formData.actualQty) || 0,
          remarks: formData.remark || "",
        },
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
        // This is an edit of an existing entry with MongoDB ID
        try {
          const getResponse = await axios.get(
            `${url}/api/dpr/fetch/${entry.id}`,
            config
          );

          if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
            const existingData = getResponse.data.data || getResponse.data.dpr;
            requestData._id = existingData._id;

            response = await axios.put(
              `${url}/api/dpr/update-entry/${existingData._id}`,
              requestData,
              config
            );
          }
        } catch (fetchErr) {
          // If fetch fails, create a new entry
          response = await axios.post(
            `${url}/api/dpr/add`,
            requestData,
            config
          );
        }
      } else {
        // This is a NEW entry or EDIT of a prefilled entry
        // Prefilled entries need to be created as new entries
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      // FIX: Check response properly
      if (response && (response.status === 200 || response.status === 201 || response.data.success)) {
        setSuccess(true);

        // Prepare the new entry object
        const newEntry = {
          id: response.data.data?._id || response.data?._id || entry?.id || `temp-${Date.now()}`,
          equipment: formData.equipmentName,
          supplier: formData.supplierName,
          startReading: Number(formData.startReading),
          endReading: Number(formData.endReading),
          inTime: timeIn ? format(timeIn, "hh:mm a") : "",
          outTime: timeOut ? format(timeOut, "hh:mm a") : "",
          working: workingHoursValue,
          planned: formData.plannedQty,
          actual: formData.actualQty,
          remark: formData.remark || "-",
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        // Use showToast for success message
        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Machinery entry updated successfully!" : "Machinery entry added successfully!"
          });
        }

        setTimeout(() => {
          if (refreshData) {
            refreshData();
          }
          if (onCancel) {
            onCancel();
          }
        }, 1500);
      } else {
        throw new Error("Failed to save entry");
      }
    } catch (err) {
      console.error("Error saving Machinery DPR:", err);
      const errorMsg = err.response?.data?.message || err.message || "Failed to save DPR entry";
      setError(errorMsg);

      // Use showToast for error message
      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input": {
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.PRIMARY,
      padding: "10px 12px",
    },
    "& input::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Entry in Machinery Report:" : "Add New Entry in Machinery Report:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* ROW 1 */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Equipment Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Equipment Name"
            value={formData.equipmentName}
            onChange={(e) => setFormData({ ...formData, equipmentName: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Supplier Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Supplier Name"
            value={formData.supplierName}
            onChange={(e) => setFormData({ ...formData, supplierName: e.target.value })}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* ROW 2 - Readings */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Start Reading
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="0"
            value={formData.startReading}
            onChange={(e) => setFormData({ ...formData, startReading: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            End Reading
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="0"
            value={formData.endReading}
            onChange={(e) => setFormData({ ...formData, endReading: e.target.value })}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* ROW 3 - Time Pickers */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            In Time
          </Typography>
          <TimePicker
            value={timeIn}
            onChange={setTimeIn}
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Out Time
          </Typography>
          <TimePicker
            value={timeOut}
            onChange={setTimeOut}
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Working Hours
          </Typography>
          <TextField
            fullWidth
            placeholder="Auto-calculated"
            value={workingHours}
            InputProps={{ readOnly: true }}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* ROW 3 */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Planned QTY
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="Planned QTY"
            value={formData.plannedQty}
            onChange={(e) => setFormData({ ...formData, plannedQty: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Actual QTY
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="Actual QTY"
            value={formData.actualQty}
            onChange={(e) => setFormData({ ...formData, actualQty: e.target.value })}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* ROW 4 – Remark */}
      <Box mb={3} width="50%">
        <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
          Remark
        </Typography>
        <TextField
          fullWidth
          multiline
          placeholder="Remark Text Goes Here"
          value={formData.remark}
          onChange={(e) => setFormData({ ...formData, remark: e.target.value })}
          sx={{
            ...inputSx,
            "& .MuiOutlinedInput-root": {
              height: 44,
            },
          }}
        />
      </Box>

      {/* BUTTON */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                equipmentName: entry?.equipment || "",
                supplierName: entry?.supplier || "",
                startReading: entry?.startReading || 0,
                endReading: entry?.endReading || 0,
                plannedQty: entry?.planned || 0,
                actualQty: entry?.actual || 0,
                remark: entry?.remark || "",
              });
              setTimeIn(null);
              setTimeOut(null);
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            px: 2.5,
            height: 44,
            borderRadius: "7px",
            "&:hover": {
              backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            },
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Saving..." : "Adding...") : success ? (isEditMode ? "Saved!" : "Added!") : (isEditMode ? "Save Changes" : "Add New Entry")}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= QUALITY CONTROL ========================= */

function QualityControlSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-quality-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && Object.values(parsed).some(v => v)) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry) => {
    onUpdateEntry(updatedEntry);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const getRemarkCellStyles = () => ({
    ...TYPOGRAPHY.BODY_LARGE,
    color: COLORS.SECONDARY,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    display: 'block',
    lineHeight: 1.4,
    marginTop: '4px',
  });

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Quality Control & Testing
      </Typography>

      {/* TABLE */}
      <Box>
        {/* HEADER - MINIMAL CHANGE with padding */}
        <FlexRow>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Test Description</Typography>
          </FlexCell>
          <FlexCell flex={2.2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Location / Element</Typography>
          </FlexCell>
          <FlexCell w="140px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Date</Typography>
          </FlexCell>
          <FlexCell w="80px" sx={{ pl: 3 }}> {/* Increased padding for more spacing */}
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>No of Test</Typography>
          </FlexCell>
          <FlexCell flex={1.5} sx={{ pl: 1 }}> {/* ADDED: pl: 1 for 8px padding */}
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Results</Typography>
          </FlexCell>
          <FlexCell w="100px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Status</Typography>
          </FlexCell>
          <FlexCell flex={1.5}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {/* ROWS - Updated with same padding */}
        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}</Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.description || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell flex={2.2}>
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.location || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="140px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.date || "-"}</Typography>
                </FlexCell>
                <FlexCell w="80px" sx={{ pl: 3 }}> {/* Match header padding */}
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.count}</Typography>
                </FlexCell>
                <FlexCell flex={1.5} sx={{ pl: 1 }}> {/* Match header padding */}
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.result || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="100px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.status || "-"}</Typography>
                </FlexCell>
                <FlexCell flex={1.5}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.SUCCESS}15`,
                          }
                        }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.ERROR}15`,
                          }
                        }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {/* EDIT FORM - Simplified without background/border */}
              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddQualityForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}
      </Box>

      {/* ADD BUTTON - Always show if not editing */}
      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddQualityForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddQualityForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-quality-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      testDescription: entry?.description || "",
      locationElement: entry?.location || "",
      testDate: entry?.date ?
        new Date(entry.date.split(' ').reverse().join('-')).toISOString().split('T')[0] :
        format(dprDate, "yyyy-MM-dd"),
      numberOfTests: entry?.count || 0,
      results: entry?.result || "",
      status: entry?.status || "",
      remarks: entry?.remark || "",
    }
  );

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Quality Control & Testing",
        title: formData.testDescription ?
          `Quality Test - ${formData.testDescription.substring(0, 10)}${formData.testDescription.length > 10 ? '...' : ''}` :
          "Quality Test Report",
        adminRemarks: formData.remarks || "", // Map remarks to adminRemarks in the request
        reportDate: format(dprDate, "yyyy-MM-dd"),
        qualityControlDetails: {
          testDescription: formData.testDescription || "",
          locationElement: formData.locationElement || "",
          testDate: formData.testDate ? new Date(formData.testDate).toISOString() : new Date().toISOString(),
          numberOfTests: Number(formData.numberOfTests) || 0,
          results: formData.results || "",
          status: formData.status || "PASS",
          remarks: formData.remarks || "", // Also include in qualityControlDetails for consistency
        }
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
        try {
          const getResponse = await axios.get(
            `${url}/api/dpr/fetch/${entry.id}`,
            config
          );

          if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
            const existingData = getResponse.data.data || getResponse.data.dpr;
            requestData._id = existingData._id;

            console.log('Sending quality control update request:', {
              url: `${url}/api/dpr/update-entry/${existingData._id}`,
              data: requestData
            });

            response = await axios.put(
              `${url}/api/dpr/update-entry/${existingData._id}`,
              requestData,
              config
            );

            console.log('Quality control update response:', response.data);
          }
        } catch (fetchErr) {
          console.error("Error fetching existing quality control entry:", fetchErr);
          response = await axios.post(
            `${url}/api/dpr/add`,
            requestData,
            config
          );
        }
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;

        if (!responseData) {
          throw new Error("Invalid response format from server");
        }

        setSuccess(true);

        const newEntry = {
          id: responseData._id || entry?.id || `temp-${Date.now()}`,
          testDescription: responseData.qualityControlDetails?.testDescription || formData.testDescription,
          location: responseData.qualityControlDetails?.locationElement || formData.locationElement,
          testDate: responseData.qualityControlDetails?.testDate || formData.testDate,
          numberOfTests: responseData.qualityControlDetails?.numberOfTests || formData.numberOfTests,
          results: responseData.qualityControlDetails?.results || formData.results,
          status: responseData.qualityControlDetails?.status || formData.status,
          remark: responseData.adminRemarks ||
            responseData.qualityControlDetails?.remarks ||
            formData.remarks ||
            "",
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Quality control entry updated successfully!" : "Quality control entry added successfully!"
          });
        }

        // Clear persistence on success
        clearFormData();

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      } else {
        throw new Error(response?.data?.message || "Failed to save quality control entry");
      }
    } catch (err) {
      console.error("Error saving Quality Control DPR:", {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMsg = "Failed to save quality control entry";
      if (err.response) {
        const responseData = err.response.data;
        if (typeof responseData === 'string') {
          errorMsg = responseData;
        } else if (responseData.message) {
          errorMsg = responseData.message;
        } else if (responseData.dpr?.error) {
          errorMsg = responseData.dpr.error;
        } else {
          errorMsg = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMsg = 'No response from server. Please check your connection.';
      } else {
        errorMsg = err.message || errorMsg;
      }

      setError(errorMsg);

      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input, & textarea": {
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.PRIMARY,
      padding: "10px 12px",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Entry in Quality Control & Testing:" : "Add New Entry in Quality Control & Testing:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* ROW 1 */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Test Description
          </Typography>
          <TextField
            fullWidth
            placeholder="Test Description"
            value={formData.testDescription}
            onChange={(e) => setFormData({ ...formData, testDescription: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Location / Element
          </Typography>
          <TextField
            fullWidth
            placeholder="Location / Element"
            value={formData.locationElement}
            onChange={(e) => setFormData({ ...formData, locationElement: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Date
          </Typography>
          <TextField
            fullWidth
            type="date"
            value={formData.testDate}
            onChange={(e) => setFormData({ ...formData, testDate: e.target.value })}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* ROW 2 */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            No of Tests
          </Typography>
          <TextField
            fullWidth
            type="number"
            placeholder="No of Tests"
            value={formData.numberOfTests}
            onChange={(e) => setFormData({ ...formData, numberOfTests: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Results
          </Typography>
          <TextField
            fullWidth
            placeholder="Results"
            value={formData.results}
            onChange={(e) => setFormData({ ...formData, results: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Status Pass / Fail
          </Typography>
          <TextField
            fullWidth
            value={formData.status}
            onChange={(e) => setFormData({ ...formData, status: e.target.value })}
            placeholder="Enter status (e.g., Pass, Fail, Pending)"
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* ROW 3 – REMARK */}
      <Box display="flex" gap={3} mb={3}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Remark
          </Typography>
          <TextField
            fullWidth
            label="Remarks"
            name="remarks"
            value={formData.remarks || ""}
            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </Box>
      </Box>

      {/* BUTTON */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                testDescription: entry?.description || "",
                locationElement: entry?.location || "",
                testDate: entry?.date ?
                  new Date(entry.date.split(' ').reverse().join('-')).toISOString().split('T')[0] :
                  format(dprDate, "yyyy-MM-dd"),
                numberOfTests: entry?.count || 0,
                results: entry?.result || "",
                status: entry?.status || "",
                remarks: entry?.remark || "",
              });
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            px: 2.5,
            height: 44,
            borderRadius: "7px",
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Saving..." : "Adding...") : success ? (isEditMode ? "Saved!" : "Added!") : (isEditMode ? "Save Changes" : "Add New Entry")}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= SAFETY OBSERVATIONS ========================= */

function SafetyObservationsSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-safety-observation-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && Object.values(parsed).some(v => v)) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry) => {
    onUpdateEntry(updatedEntry);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const getRemarkCellStyles = () => ({
    ...TYPOGRAPHY.BODY_LARGE,
    color: COLORS.SECONDARY,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    display: 'block',
    lineHeight: 1.4,
    marginTop: '4px',
  });

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Safety Observations
      </Typography>

      {/* TABLE */}
      <Box>
        {/* HEADER */}
        <FlexRow>
          <FlexCell w="40px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Item</Typography>
          </FlexCell>
          <FlexCell w="120px">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Status</Typography>
          </FlexCell>
          <FlexCell flex={2}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
            </Box>
          </FlexCell>
        </FlexRow>

        {/* ROWS */}
        {entries.length === 0 ? (
          <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
            <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
              No entries yet
            </Typography>
          </Box>
        ) : (
          entries.map((row, i) => (
            <React.Fragment key={row.id || i}>
              <FlexRow>
                <FlexCell w="40px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}</Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Typography sx={{
                    ...TYPOGRAPHY.BODY_LARGE,
                    color: COLORS.SECONDARY,
                    wordWrap: "break-word",
                    whiteSpace: "normal",
                    lineHeight: 1.4
                  }}>
                    {row.item || "-"}
                  </Typography>
                </FlexCell>
                <FlexCell w="120px">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.status || "-"}</Typography>
                </FlexCell>
                <FlexCell flex={2}>
                  <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                    <Typography sx={getRemarkCellStyles()}>
                      {row.remark}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton
                        size="small"
                        onClick={() => handleEditEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.SUCCESS}15`,
                          }
                        }}
                      >
                        <Icon name="Action3" color="#8ED081" size={20} />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteEntry(row)}
                        sx={{
                          padding: 0.5,
                          "&:hover": {
                            backgroundColor: `${COLORS.ERROR}15`,
                          }
                        }}
                      >
                        <Icon name="close2" color={COLORS.ERROR} size={20} />
                      </IconButton>
                    </Box>
                  </Box>
                </FlexCell>
              </FlexRow>

              {/* EDIT FORM - Simplified without background/border */}
              {editingId === row.id && (
                <Box sx={{ mt: 2, mb: 2 }}>
                  <AddSafetyObservationForm
                    entry={row}
                    onCancel={handleCancelEdit}
                    projectId={projectId}
                    dprDate={dprDate}
                    onSuccess={handleEditSuccess}
                    refreshData={refreshData}
                    showToast={showToast}
                  />
                </Box>
              )}
            </React.Fragment>
          ))
        )}
      </Box>

      {/* ADD BUTTON - Always show if not editing */}
      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddSafetyObservationForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddSafetyObservationForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  // FIX: Initialize formData correctly with remarks field
  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-safety-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      itemName: entry?.item || entry?.itemName || "",
      status: entry?.status || "",
      remarks: entry?.remark || entry?.remarks || "",
    }
  );

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!formData.itemName.trim()) {
        setError("Item name is required");
        return;
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Safety Observation",
        title: formData.itemName ?
          `Safety - ${formData.itemName.substring(0, 10)}${formData.itemName.length > 10 ? '...' : ''}` :
          "Safety Observation Report",
        adminRemarks: formData.remarks || "", // FIX: Use formData.remarks
        reportDate: format(dprDate, "yyyy-MM-dd"),
        safetyObservationDetails: {
          itemName: formData.itemName || "",
          status: formData.status || "YES",
          remarks: formData.remarks || "", // FIX: Use formData.remarks
        }
      };

      console.log("Sending safety observation data:", requestData);

      let response;

      if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
        try {
          const getResponse = await axios.get(
            `${url}/api/dpr/fetch/${entry.id}`,
            config
          );

          if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
            const existingData = getResponse.data.data || getResponse.data.dpr;
            requestData._id = existingData._id;

            console.log('Sending safety observation update request:', {
              url: `${url}/api/dpr/update-entry/${existingData._id}`,
              data: requestData
            });

            response = await axios.put(
              `${url}/api/dpr/update-entry/${existingData._id}`,
              requestData,
              config
            );

            console.log('Safety observation update response:', response.data);
          }
        } catch (fetchErr) {
          console.error("Error fetching existing safety observation entry:", fetchErr);
          response = await axios.post(
            `${url}/api/dpr/add`,
            requestData,
            config
          );
        }
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;

        if (!responseData) {
          throw new Error("Invalid response format from server");
        }

        setSuccess(true);

        const newEntry = {
          id: responseData._id || entry?.id || `temp-${Date.now()}`,
          item: responseData.safetyObservationDetails?.itemName || formData.itemName,
          itemName: responseData.safetyObservationDetails?.itemName || formData.itemName,
          status: responseData.safetyObservationDetails?.status || formData.status,
          remark: responseData.adminRemarks ||
            responseData.safetyObservationDetails?.remarks ||
            formData.remarks ||
            "",
          remarks: responseData.adminRemarks ||
            responseData.safetyObservationDetails?.remarks ||
            formData.remarks ||
            "",
        };

        console.log("Created/Updated entry:", newEntry);

        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Safety observation updated successfully!" : "Safety observation added successfully!"
          });
        }

        // Clear persistence on success
        clearFormData();

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      } else {
        throw new Error(response?.data?.message || "Failed to save safety observation");
      }
    } catch (err) {
      console.error("Error saving Safety Observation DPR:", {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMsg = "Failed to save safety observation";
      if (err.response) {
        const responseData = err.response.data;
        if (typeof responseData === 'string') {
          errorMsg = responseData;
        } else if (responseData.message) {
          errorMsg = responseData.message;
        } else if (responseData.dpr?.error) {
          errorMsg = responseData.dpr.error;
        } else {
          errorMsg = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMsg = 'No response from server. Please check your connection.';
      } else {
        errorMsg = err.message || errorMsg;
      }

      setError(errorMsg);

      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input": {
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.PRIMARY,
      padding: "10px 12px",
    },
    "& input::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  // Helper function to handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Entry in Safety Observations:" : "Add New Entry in Safety Observations:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* SINGLE ROW */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Item
          </Typography>
          <TextField
            fullWidth
            placeholder="Item"
            value={formData.itemName}
            onChange={(e) => handleInputChange('itemName', e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Status
          </Typography>
          <TextField
            fullWidth
            value={formData.status}
            onChange={(e) => handleInputChange('status', e.target.value)}
            placeholder="Enter status (e.g., Yes, No, NA)"
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Remark
          </Typography>
          <TextField
            fullWidth
            placeholder="Remark"
            value={formData.remarks} // FIX: Changed from formData.remark to formData.remarks
            onChange={(e) => handleInputChange('remarks', e.target.value)} // FIX: Changed from 'remark' to 'remarks'
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* BUTTON */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                itemName: entry?.item || entry?.itemName || "",
                status: entry?.status || "",
                remarks: entry?.remark || entry?.remarks || "",
              });
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success || !formData.itemName.trim()}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            px: 2.5,
            height: 44,
            borderRadius: "7px",
            "&:hover": {
              backgroundColor: COLORS.SECONDARY,
            },
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Saving..." : "Adding...") :
            success ? (isEditMode ? "Saved!" : "Added!") :
              (isEditMode ? "Save Changes" : "Add New Entry")}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= VISITOR LOGS ========================= */

function VisitorLogsSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-visitor-log-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && Object.values(parsed).some(v => v)) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry) => {
    onUpdateEntry(updatedEntry);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  const getRemarkCellStyles = () => ({
    ...TYPOGRAPHY.BODY_LARGE,
    color: COLORS.SECONDARY,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    display: 'block',
    lineHeight: 1.4,
    marginTop: '4px',
  });

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Visitor Logs
      </Typography>

      {/* HEADER */}
      <FlexRow sx={{ pb: 1 }}>
        <FlexCell w="40px">
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
        </FlexCell>
        <FlexCell flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Visitor Name</Typography>
        </FlexCell>
        <FlexCell flex={1.2}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Purpose of Visit</Typography>
        </FlexCell>
        <FlexCell flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Instruction / Decisions</Typography>
        </FlexCell>
        <FlexCell w="90px">
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time In</Typography>
        </FlexCell>
        <FlexCell w="90px">
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Time Out</Typography>
        </FlexCell>
        <FlexCell flex={1.5}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
          </Box>
        </FlexCell>
      </FlexRow>

      {/* ROWS */}
      {entries.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
            No entries yet
          </Typography>
        </Box>
      ) : (
        entries.map((row, i) => (
          <React.Fragment key={row.id || i}>
            <FlexRow sx={{ py: 1 }}>
              <FlexCell w="40px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}</Typography>
              </FlexCell>
              <FlexCell flex={1.5}>
                <Box display="flex" flexDirection="column">
                  <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>
                    {row.name || "-"}
                  </Typography>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT_SECONDARY, lineHeight: 1.3 }}>
                    {row.company || "-"}
                  </Typography>
                  <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT_SECONDARY, lineHeight: 1.3 }}>
                    {row.designation || "-"}
                  </Typography>
                </Box>
              </FlexCell>
              <FlexCell flex={1.2}>
                <Typography sx={{
                  ...TYPOGRAPHY.BODY_LARGE,
                  color: COLORS.SECONDARY,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.4
                }}>
                  {row.purpose || "-"}
                </Typography>
              </FlexCell>
              <FlexCell flex={1.5}>
                <Typography sx={{
                  ...TYPOGRAPHY.BODY_LARGE,
                  color: COLORS.SECONDARY,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.4
                }}>
                  {row.instructions || "-"}
                </Typography>
              </FlexCell>
              <FlexCell w="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.timeIn || "-"}</Typography>
              </FlexCell>
              <FlexCell w="90px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.timeOut || "-"}</Typography>
              </FlexCell>
              <FlexCell flex={1.5}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography sx={getRemarkCellStyles()}>
                    {row.remark}
                  </Typography>
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditEntry(row)}
                      sx={{
                        padding: 0.5,
                        "&:hover": {
                          backgroundColor: `${COLORS.SUCCESS}15`,
                        }
                      }}
                    >
                      <Icon name="Action3" color="#8ED081" size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteEntry(row)}
                      sx={{
                        padding: 0.5,
                        "&:hover": {
                          backgroundColor: `${COLORS.ERROR}15`,
                        }
                      }}
                    >
                      <Icon name="close2" color={COLORS.ERROR} size={20} />
                    </IconButton>
                  </Box>
                </Box>
              </FlexCell>
            </FlexRow>

            {/* EDIT FORM - Simplified without background/border */}
            {editingId === row.id && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <AddVisitorLogForm
                  entry={row}
                  onCancel={handleCancelEdit}
                  projectId={projectId}
                  dprDate={dprDate}
                  onSuccess={handleEditSuccess}
                  refreshData={refreshData}
                  showToast={showToast}
                />
              </Box>
            )}
          </React.Fragment>
        ))
      )}

      {/* ADD BUTTON - Always show if not editing */}
      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddVisitorLogForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddVisitorLogForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [visitorTimeIn, setVisitorTimeIn] = useState(null);
  const [visitorTimeOut, setVisitorTimeOut] = useState(null);

  const isEditMode = !!entry;

  // Parse time strings to Date objects for edit mode
  useEffect(() => {
    if (isEditMode && entry?.timeIn && entry.timeIn !== "-") {
      const timeString = entry.timeIn;
      const [time, modifier] = timeString.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }

      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
      setVisitorTimeIn(date);
    }

    if (isEditMode && entry?.timeOut && entry.timeOut !== "-") {
      const timeString = entry.timeOut;
      const [time, modifier] = timeString.split(' ');
      let [hours, minutes] = time.split(':');

      if (modifier === 'PM' && hours !== '12') {
        hours = parseInt(hours, 10) + 12;
      }
      if (modifier === 'AM' && hours === '12') {
        hours = '00';
      }

      const date = new Date();
      date.setHours(parseInt(hours, 10), parseInt(minutes, 10), 0);
      setVisitorTimeOut(date);
    }
  }, [isEditMode, entry]);

  // FIX: Initialize formData with all required fields
  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-visitor-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      visitorName: entry?.name || entry?.visitorName || "",
      companyName: entry?.company || entry?.companyName || "",
      designation: entry?.designation || "",
      purposeOfVisit: entry?.purpose || entry?.purposeOfVisit || "",
      instructionsDecisions: entry?.instructions || entry?.instructionsDecisions || "",
      remarks: entry?.remark || entry?.remarks || ""
    }
  );

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!formData.visitorName.trim()) {
        setError("Visitor name is required");
        return;
      }

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      // Format times from TimePicker or use default
      const formatTimeFromPicker = (time) => {
        if (!time) return "09:00 AM";
        return format(time, "hh:mm a");
      };

      const timeIn = visitorTimeIn ? formatTimeFromPicker(visitorTimeIn) :
        (entry?.timeIn && entry.timeIn !== "-" ? entry.timeIn : "09:00 AM");

      const timeOut = visitorTimeOut ? formatTimeFromPicker(visitorTimeOut) :
        (entry?.timeOut && entry.timeOut !== "-" ? entry.timeOut : "05:00 PM");

      const requestData = {
        projectId,
        reportType: "Visitor Log",
        title: formData.visitorName ?
          `Visitor - ${formData.visitorName.substring(0, 12)}${formData.visitorName.length > 12 ? '...' : ''}` :
          "Visitor Log Entry",
        adminRemarks: formData.remarks || "",
        reportDate: format(dprDate, "yyyy-MM-dd"),
        visitorLogDetails: {
          visitorName: formData.visitorName || "",
          companyName: formData.companyName || "", // FIX: Add companyName
          designation: formData.designation || "", // FIX: Add designation
          purposeOfVisit: formData.purposeOfVisit || "",
          instructionsDecisions: formData.instructionsDecisions || "",
          timeIn: timeIn,
          timeOut: timeOut,
          remarks: formData.remarks || ""
        }
      };

      console.log("Sending visitor log data:", requestData);

      let response;

      if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
        try {
          const getResponse = await axios.get(
            `${url}/api/dpr/fetch/${entry.id}`,
            config
          );

          if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
            const existingData = getResponse.data.data || getResponse.data.dpr;
            requestData._id = existingData._id;

            console.log('Sending visitor log update request:', {
              url: `${url}/api/dpr/update-entry/${existingData._id}`,
              data: requestData
            });

            response = await axios.put(
              `${url}/api/dpr/update-entry/${existingData._id}`,
              requestData,
              config
            );

            console.log('Visitor log update response:', response.data);
          }
        } catch (fetchErr) {
          console.error("Error fetching existing visitor log entry:", fetchErr);
          response = await axios.post(
            `${url}/api/dpr/add`,
            requestData,
            config
          );
        }
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;

        if (!responseData) {
          throw new Error("Invalid response format from server");
        }

        setSuccess(true);

        const newEntry = {
          id: responseData._id || entry?.id || `temp-${Date.now()}`,
          name: responseData.visitorLogDetails?.visitorName || formData.visitorName,
          visitorName: responseData.visitorLogDetails?.visitorName || formData.visitorName,
          company: responseData.visitorLogDetails?.companyName || formData.companyName,
          companyName: responseData.visitorLogDetails?.companyName || formData.companyName,
          designation: responseData.visitorLogDetails?.designation || formData.designation,
          purpose: responseData.visitorLogDetails?.purposeOfVisit || formData.purposeOfVisit,
          purposeOfVisit: responseData.visitorLogDetails?.purposeOfVisit || formData.purposeOfVisit,
          instructions: responseData.visitorLogDetails?.instructionsDecisions || formData.instructionsDecisions,
          instructionsDecisions: responseData.visitorLogDetails?.instructionsDecisions || formData.instructionsDecisions,
          timeIn: responseData.visitorLogDetails?.timeIn || timeIn,
          timeOut: responseData.visitorLogDetails?.timeOut || timeOut,
          remark: responseData.adminRemarks ||
            responseData.visitorLogDetails?.remarks ||
            formData.remarks ||
            "",
          remarks: responseData.adminRemarks ||
            responseData.visitorLogDetails?.remarks ||
            formData.remarks ||
            "",
        };

        console.log("Created/Updated entry:", newEntry);

        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Visitor log updated successfully!" : "Visitor log added successfully!"
          });
        }

        // Clear persistence on success
        clearFormData();

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      } else {
        throw new Error(response?.data?.message || "Failed to save visitor log");
      }
    } catch (err) {
      console.error("Error saving Visitor Log DPR:", {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMsg = "Failed to save visitor log";
      if (err.response) {
        const responseData = err.response.data;
        if (typeof responseData === 'string') {
          errorMsg = responseData;
        } else if (responseData.message) {
          errorMsg = responseData.message;
        } else if (responseData.dpr?.error) {
          errorMsg = responseData.dpr.error;
        } else {
          errorMsg = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMsg = 'No response from server. Please check your connection.';
      } else {
        errorMsg = err.message || errorMsg;
      }

      setError(errorMsg);

      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input, & textarea": {
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.PRIMARY,
      padding: "10px 12px",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  // Helper function to handle input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Entry in Visitor Logs:" : "Add New Entry in Visitor Logs:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* ROW 1 */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Visitor Name *
          </Typography>
          <TextField
            fullWidth
            placeholder="Visitor Name"
            value={formData.visitorName}
            onChange={(e) => handleInputChange('visitorName', e.target.value)}
            sx={inputSx}
            required
          />
        </Box>

        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Company Name
          </Typography>
          <TextField
            fullWidth
            placeholder="Company Name"
            value={formData.companyName}
            onChange={(e) => handleInputChange('companyName', e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Designation
          </Typography>
          <TextField
            fullWidth
            placeholder="Designation"
            value={formData.designation}
            onChange={(e) => handleInputChange('designation', e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Purpose of Visit *
          </Typography>
          <TextField
            fullWidth
            placeholder="Purpose of Visit"
            value={formData.purposeOfVisit}
            onChange={(e) => handleInputChange('purposeOfVisit', e.target.value)}
            sx={inputSx}
            required
          />
        </Box>
      </Box>

      {/* ROW 2 */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Instruction / Decisions
          </Typography>
          <TextField
            fullWidth
            placeholder="Instruction / Decisions"
            value={formData.instructionsDecisions}
            onChange={(e) => handleInputChange('instructionsDecisions', e.target.value)}
            sx={inputSx}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Time In
          </Typography>
          <TimePicker
            value={visitorTimeIn}
            onChange={setVisitorTimeIn}
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
        </Box>

        <Box flex={1}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Time Out
          </Typography>
          <TimePicker
            value={visitorTimeOut}
            onChange={setVisitorTimeOut}
            renderInput={(params) => <TextField {...params} fullWidth sx={inputSx} />}
          />
        </Box>
      </Box>

      {/* ROW 3 – REMARK */}
      <Box display="flex" gap={3} mb={3}>
        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Remark
          </Typography>
          <TextField
            fullWidth
            placeholder="Remark"
            value={formData.remarks || ""}
            onChange={(e) => handleInputChange('remarks', e.target.value)}
            multiline
            rows={2}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* BUTTONS */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                visitorName: entry?.name || entry?.visitorName || "",
                companyName: entry?.company || entry?.companyName || "",
                designation: entry?.designation || "",
                purposeOfVisit: entry?.purpose || entry?.purposeOfVisit || "",
                instructionsDecisions: entry?.instructions || entry?.instructionsDecisions || "",
                remarks: entry?.remark || entry?.remarks || ""
              });
              setVisitorTimeIn(null);
              setVisitorTimeOut(null);
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success || !formData.visitorName.trim() || !formData.purposeOfVisit.trim()}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            px: 2.5,
            height: 44,
            borderRadius: "7px",
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Saving..." : "Adding...") :
            success ? (isEditMode ? "Saved!" : "Added!") :
              (isEditMode ? "Save Changes" : "Add New Entry")}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= DELAYS & HINDRANCES ========================= */

function DelaysHindrancesSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-delay-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && Object.values(parsed).some(v => v)) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleAddEntry = (entry) => {
    onAddEntry(entry);
    setShowAddForm(false);
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleEditSuccess = (updatedEntry) => {
    onUpdateEntry(updatedEntry);
    setEditingId(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  const getRemarkCellStyles = () => ({
    ...TYPOGRAPHY.BODY_LARGE,
    color: COLORS.SECONDARY,
    wordWrap: 'break-word',
    wordBreak: 'break-word',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    maxWidth: '100%',
    display: 'block',
    lineHeight: 1.4,
    marginTop: '4px',
  });

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT, mb: 1.5 }}>
        Delays & Hindrances
      </Typography>

      {/* HEADER */}
      <FlexRow sx={{ pb: 1 }}>
        <FlexCell w="40px">
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Sr</Typography>
        </FlexCell>
        <FlexCell flex={2}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Description of Delay</Typography>
        </FlexCell>
        <FlexCell w="120px">
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Duration</Typography>
        </FlexCell>
        <FlexCell flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Reason</Typography>
        </FlexCell>
        <FlexCell flex={2}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.TEXT }}>Remark</Typography>
          </Box>
        </FlexCell>
      </FlexRow>

      {/* ROWS */}
      {entries.length === 0 ? (
        <Box sx={{ py: 3, textAlign: 'center', borderBottom: `1px solid ${COLORS.LIGHT_SECONDARY}` }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY }}>
            No entries yet
          </Typography>
        </Box>
      ) : (
        entries.map((row, i) => (
          <React.Fragment key={row.id || i}>
            <FlexRow sx={{ py: 1 }}>
              <FlexCell w="40px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{i + 1}</Typography>
              </FlexCell>
              <FlexCell flex={2}>
                <Typography sx={{
                  ...TYPOGRAPHY.BODY_LARGE,
                  color: COLORS.SECONDARY,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.4
                }}>
                  {row.description || "-"}
                </Typography>
              </FlexCell>
              <FlexCell w="120px">
                <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY }}>{row.duration || "-"}</Typography>
              </FlexCell>
              <FlexCell flex={1.5}>
                <Typography sx={{
                  ...TYPOGRAPHY.BODY_LARGE,
                  color: COLORS.SECONDARY,
                  wordWrap: "break-word",
                  whiteSpace: "normal",
                  lineHeight: 1.4
                }}>
                  {row.reason || "-"}
                </Typography>
              </FlexCell>
              <FlexCell flex={2}>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <Typography sx={getRemarkCellStyles()}>
                    {row.remark}
                  </Typography>
                  <Box display="flex" gap={0.5}>
                    <IconButton
                      size="small"
                      onClick={() => handleEditEntry(row)}
                      sx={{
                        padding: 0.5,
                        "&:hover": {
                          backgroundColor: `${COLORS.SUCCESS}15`,
                        }
                      }}
                    >
                      <Icon name="Action3" color="#8ED081" size={20} />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteEntry(row)}
                      sx={{
                        padding: 0.5,
                        "&:hover": {
                          backgroundColor: `${COLORS.ERROR}15`,
                        }
                      }}
                    >
                      <Icon name="close2" color={COLORS.ERROR} size={20} />
                    </IconButton>
                  </Box>
                </Box>
              </FlexCell>
            </FlexRow>

            {/* EDIT FORM - Simplified without background/border */}
            {editingId === row.id && (
              <Box sx={{ mt: 2, mb: 2 }}>
                <AddDelayForm
                  entry={row}
                  onCancel={handleCancelEdit}
                  projectId={projectId}
                  dprDate={dprDate}
                  onSuccess={handleEditSuccess}
                  refreshData={refreshData}
                  showToast={showToast}
                />
              </Box>
            )}
          </React.Fragment>
        ))
      )}

      {/* ADD BUTTON - Always show if not editing */}
      {!editingId && (
        <Box display="flex" justifyContent="flex-end" mt={1.5}>
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
            }}
          >
            Add New Entry
          </Button>
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2}>
          <AddDelayForm
            onCancel={() => setShowAddForm(false)}
            projectId={projectId}
            dprDate={dprDate}
            onAddEntry={handleAddEntry}
            refreshData={refreshData}
            showToast={showToast}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddDelayForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const isEditMode = !!entry;

  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-delay-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      descriptionOfDelay: entry?.description || "",
      duration: entry?.duration || "",
      reason: entry?.reason || "",
      remarks: entry?.remark || ""
    }
  );

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      };

      const requestData = {
        projectId,
        reportType: "Delay Hindrances",
        title: formData.descriptionOfDelay ?
          `Delay - ${formData.descriptionOfDelay.substring(0, 20)}` :
          "Delay Report",
        adminRemarks: formData.remarks || "",
        reportDate: format(dprDate, "yyyy-MM-dd"),
        delayHindrancesDetails: {
          descriptionOfDelay: formData.descriptionOfDelay || "",
          duration: formData.duration || "",
          reason: formData.reason || "",
          remarks: formData.remarks || ""
        }
      };

      let response;

      if (isEditMode && entry?.id && !entry.id.startsWith('prefill-')) {
        try {
          const getResponse = await axios.get(
            `${url}/api/dpr/fetch/${entry.id}`,
            config
          );

          if (getResponse.data && (getResponse.data.data || getResponse.data.dpr)) {
            const existingData = getResponse.data.data || getResponse.data.dpr;
            requestData._id = existingData._id;

            console.log('Sending delay hindrance update request:', {
              url: `${url}/api/dpr/update-entry/${existingData._id}`,
              data: requestData
            });

            response = await axios.put(
              `${url}/api/dpr/update-entry/${existingData._id}`,
              requestData,
              config
            );

            console.log('Delay hindrance update response:', response.data);
          }
        } catch (fetchErr) {
          console.error("Error fetching existing delay hindrance entry:", fetchErr);
          response = await axios.post(
            `${url}/api/dpr/add`,
            requestData,
            config
          );
        }
      } else {
        response = await axios.post(
          `${url}/api/dpr/add`,
          requestData,
          config
        );
      }

      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;

        if (!responseData) {
          throw new Error("Invalid response format from server");
        }

        setSuccess(true);

        const newEntry = {
          id: responseData._id || entry?.id || `temp-${Date.now()}`,
          description: responseData.delayHindrancesDetails?.descriptionOfDelay || formData.descriptionOfDelay,
          duration: responseData.delayHindrancesDetails?.duration || formData.duration,
          reason: responseData.delayHindrancesDetails?.reason || formData.reason,
          remarks: responseData.adminRemarks ||
            responseData.delayHindrancesDetails?.remarks ||
            formData.remarks ||
            "",
        };

        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Delay hindrance updated successfully!" : "Delay hindrance added successfully!"
          });
        }

        // Clear persistence on success
        clearFormData();

        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);
      } else {
        throw new Error(response?.data?.message || "Failed to save delay hindrance");
      }
    } catch (err) {
      console.error("Error saving Delay Hindrance DPR:", {
        error: err,
        response: err.response?.data,
        status: err.response?.status,
      });

      let errorMsg = "Failed to save delay hindrance";
      if (err.response) {
        const responseData = err.response.data;
        if (typeof responseData === 'string') {
          errorMsg = responseData;
        } else if (responseData.message) {
          errorMsg = responseData.message;
        } else if (responseData.dpr?.error) {
          errorMsg = responseData.dpr.error;
        } else {
          errorMsg = `Server error: ${err.response.status}`;
        }
      } else if (err.request) {
        errorMsg = 'No response from server. Please check your connection.';
      } else {
        errorMsg = err.message || errorMsg;
      }

      setError(errorMsg);

      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input, & textarea": {
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.PRIMARY,
      padding: "10px 12px",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Entry in Delays & Hindrances:" : "Add New Entry in Delays & Hindrances:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* ROW 1 */}
      <Box display="flex" gap={3} mb={2}>
        <Box flex={2}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Description of Delay
          </Typography>
          <TextField
            fullWidth
            placeholder="Description of Delay"
            value={formData.descriptionOfDelay}
            onChange={(e) => setFormData({ ...formData, descriptionOfDelay: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box width="120px">
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Duration
          </Typography>
          <TextField
            fullWidth
            placeholder="Duration"
            value={formData.duration}
            onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
            sx={inputSx}
          />
        </Box>

        <Box flex={1.5}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Reason
          </Typography>
          <TextField
            fullWidth
            placeholder="Reason"
            value={formData.reason}
            onChange={(e) => setFormData({ ...formData, reason: e.target.value })}
            sx={inputSx}
          />
        </Box>
      </Box>

      {/* ROW 2 */}
      <Box display="flex" gap={3} mb={3}>
        <Box flex={2}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.SECONDARY, mb: 0.5 }}>
            Remark
          </Typography>
          <TextField
            fullWidth
            label="Remarks"
            name="remarks"
            value={formData.remarks || ""}
            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            margin="normal"
            multiline
            rows={2}
          />
        </Box>
      </Box>

      {/* BUTTONS */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                descriptionOfDelay: entry?.description || "",
                duration: entry?.duration || "",
                reason: entry?.reason || "",
                remarks: entry?.remark || ""
              });
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            px: 2.5,
            height: 44,
            borderRadius: "7px",
            "&:hover": {
              backgroundColor: COLORS.SECONDARY,
            },
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Saving..." : "Adding...") : success ? (isEditMode ? "Saved!" : "Added!") : (isEditMode ? "Save Changes" : "Add New Entry")}
        </Button>
      </Box>
    </Box>
  );
}

function PhotographsSection({ projectId, dprDate, entries, onAddEntry, onUpdateEntry, onDeleteEntry, refreshData }) {
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);

  // Auto-show form if draft exists
  useEffect(() => {
    const draftKey = `dpr-add-photo-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`;
    const draft = localStorage.getItem(draftKey);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        if (parsed && (parsed.activityLocation || parsed.remarks || parsed.preview || (parsed.shapes && parsed.shapes.length > 0))) {
          setShowAddForm(true);
        }
      } catch (e) {}
    }
  }, [projectId, dprDate]);

  const { token } = useAuth();
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  // Helper function to format date locally without UTC conversion
  const formatLocalDate = (date) => {
    const localDate = new Date(date);
    const year = localDate.getFullYear();
    const month = String(localDate.getMonth() + 1).padStart(2, '0');
    const day = String(localDate.getDate()).padStart(2, '0');
    const localISODate = `${year}-${month}-${day}T${String(localDate.getHours()).padStart(2, '0')}:${String(localDate.getMinutes()).padStart(2, '0')}:${String(localDate.getSeconds()).padStart(2, '0')}.${String(localDate.getMilliseconds()).padStart(3, '0')}Z`;
    return localISODate;
  };

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleEditEntry = (entry) => {
    setEditingId(entry.id);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
  };

  const handleDeleteEntry = async (entry) => {
    try {
      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
      };

      await axios.delete(`${url}/api/dpr/delete/${entry.id}`, config);
      onDeleteEntry(entry.id);

      showToast({
        type: "success",
        title: "Success",
        message: "Entry deleted successfully!"
      });
    } catch (err) {
      console.error("Error deleting entry:", err);
      showToast({
        type: "error",
        title: "Error",
        message: "Failed to delete entry"
      });
    }
  };

  // Handle saving new photo
  const handleSaveNewPhoto = async (photoData) => {
    try {
      setShowAddForm(false);

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data'
        }
      };

      // Create FormData
      const formData = new FormData();

      // Add the photo file if it exists
      if (photoData.file) {
        formData.append('photos', photoData.file);
      } else if (photoData.imageUrl && photoData.imageUrl.startsWith('data:image')) {
        // Convert base64 to blob
        const base64Response = await fetch(photoData.imageUrl);
        const blob = await base64Response.blob();
        const file = new File([blob], `photo_${Date.now()}.png`, { type: 'image/png' });
        formData.append('photos', file);
      }

      // Add required fields
      formData.append('projectId', projectId);
      formData.append('reportType', 'Photograph');
      formData.append('title', `Photograph - ${photoData.activityLocation || 'Daily Report'}`);
      formData.append('adminRemarks', photoData.remarks || '');
      formData.append('reportDate', formatLocalDate(dprDate));

      // Add photographDetails
      const photographDetails = {
        activityLocation: photoData.activityLocation || "",
        remarks: photoData.remarks || "",
        shapes: photoData.shapes || []
      };

      formData.append('photographDetails', JSON.stringify(photographDetails));

      // Send to backend
      const response = await axios.post(
        `${url}/api/dpr/add`,
        formData,
        config
      );

      if (response.status === 200 || response.status === 201) {
        const responseData = response.data.data || response.data.dpr || response.data;

        // Create new entry for local state
        const newEntry = {
          id: responseData._id,
          activity: responseData.photographDetails?.activityLocation || photoData.activityLocation,
          remark: responseData.adminRemarks || responseData.photographDetails?.remarks || photoData.remarks,
          src: responseData.photographDetails?.photoPath
            ? `${url}/${responseData.photographDetails.photoPath.replace(/\\/g, '/')}`.replace(/([^:])\/\//g, '$1/')
            : photoData.imageUrl,
          photographDetails: {
            ...responseData.photographDetails,
            shapes: photoData.shapes || []
          }
        };

        onAddEntry(newEntry);

        showToast({
          type: "success",
          title: "Success",
          message: "Photograph added successfully!"
        });

        // Don't call refreshData() here as we're already updating local state
        // This prevents the photo from disappearing briefly after upload

      } else {
        throw new Error("Failed to save photograph");
      }
    } catch (err) {
      console.error("Error saving photo:", err);
      showToast({
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to save photograph"
      });
    }
  };

  // Handle updating existing photo
  const handleUpdatePhoto = async (entryId, photoData) => {
    try {
      console.log('handleUpdatePhoto function started with:', { entryId, photoData });

      const config = {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          'Content-Type': 'multipart/form-data'
        }
      };

      const formData = new FormData();

      // Add file if it exists
      if (photoData.file) {
        formData.append('photos', photoData.file);
      }

      // Add required fields
      formData.append('projectId', projectId);
      formData.append('reportType', 'Photograph');
      formData.append('title', `Photograph - ${photoData.activityLocation || 'Updated Photo'}`);
      formData.append('adminRemarks', photoData.remarks || '');
      formData.append('reportDate', formatLocalDate(dprDate));

      // Add photographDetails
      const photographDetails = {
        activityLocation: photoData.activityLocation || "",
        remarks: photoData.remarks || "",
        shapes: photoData.shapes || []
      };

      formData.append('photographDetails', JSON.stringify(photographDetails));

      console.log('handleUpdatePhoto - About to fetch existing entry with ID:', entryId);

      // Fetch existing entry
      const getResponse = await axios.get(
        `${url}/api/dpr/fetch/${entryId}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
          }
        }
      );

      console.log('handleUpdatePhoto - Fetch response:', getResponse.data);
      console.log('handleUpdatePhoto - Fetch response structure:', JSON.stringify(getResponse.data, null, 2));
      console.log('handleUpdatePhoto - getResponse.data.dpr exists:', !!getResponse.data.dpr);

      if (getResponse.data && getResponse.data.dpr) {
        const existingData = getResponse.data.dpr;

        console.log('handleUpdatePhoto - Existing data found:', existingData._id);

        // Update the entry
        console.log('handleUpdatePhoto - Sending PUT request to:', `${url}/api/dpr/update-entry/${existingData._id}`);
        console.log('handleUpdatePhoto - FormData contents:');
        for (let [key, value] of formData.entries()) {
          console.log(`  ${key}:`, value instanceof File ? `File: ${value.name}` : value);
        }

        const response = await axios.put(
          `${url}/api/dpr/update-entry/${existingData._id}`,
          formData,
          config
        );

        console.log('handleUpdatePhoto - Update response:', response.data);
        console.log('handleUpdatePhoto - Update response status:', response.status);

        if (response.status === 200) {
          const responseData = response.data; // Fix the response data extraction
          const existingEntry = entries.find(e => e.id === entryId);

          const updatedEntry = {
            id: responseData._id,
            activity: responseData.photographDetails?.activityLocation || photoData.activityLocation,
            remark: responseData.adminRemarks || responseData.photographDetails?.remarks || photoData.remarks,
            src: responseData.photographDetails?.photoPath
              ? (responseData.photographDetails.photoPath.startsWith('http')
                ? responseData.photographDetails.photoPath.replace(/\\/g, '/')
                : `${url}/${responseData.photographDetails.photoPath.replace(/\\/g, '/')}`.replace(/([^:])\/\//g, '$1/'))
              : (photoData.imageUrl || existingEntry?.src),
            photographDetails: {
              ...responseData.photographDetails,
              activityLocation: responseData.photographDetails?.activityLocation || photoData.activityLocation,
              remarks: responseData.adminRemarks || responseData.photographDetails?.remarks || photoData.remarks,
              shapes: photoData.shapes || []
            }
          };

          console.log('Photograph update - Updated entry being sent:', updatedEntry);
          onUpdateEntry(updatedEntry);
          setEditingId(null);

          showToast({
            type: "success",
            title: "Success",
            message: "Photograph updated successfully!"
          });

          // Delay refresh to allow local state update first
          setTimeout(() => {
            if (refreshData) refreshData();
          }, 100);
        } else {
          throw new Error("Failed to update photograph");
        }
      } else {
        throw new Error("Failed to fetch existing entry");
      }
    } catch (err) {
      console.error("Error updating photo:", err);
      console.error("Error details:", {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      showToast({
        type: "error",
        title: "Error",
        message: err.response?.data?.message || "Failed to update photograph"
      });
    }
  };

  return (
    <Box mt={4} sx={{ mb: 4 }}>
      {/* Title and Add button in the same row - ALWAYS VISIBLE */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ ...TYPOGRAPHY.H3, color: COLORS.TEXT }}>
          Photographs
        </Typography>

        {/* Add button in title row - ALWAYS SHOWN except when editing */}
        {!editingId && (
          <Button
            size="small"
            startIcon={<Icon name="plus2" />}
            onClick={() => setShowAddForm(true)}
            sx={{
              backgroundColor: COLORS.SECONDARY,
              color: "#fff",
              textTransform: "none",
              borderRadius: "7px",
              px: 2,
              "&:hover": {
                backgroundColor: `${COLORS.SECONDARY}dd`,
              }
            }}
          >
            Add New Photo
          </Button>
        )}
      </Box>

      {/* PHOTOS - 2 per row on mobile, 3 on desktop */}
      {entries.length === 0 ? (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE, color: COLORS.TEXT_SECONDARY, mb: 2 }}>
            No photographs yet
          </Typography>
        </Box>
      ) : (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(1, 1fr)",
            sm: "repeat(2, 1fr)",
            md: "repeat(3, 1fr)"
          },
          gap: 3
        }}>
          {entries.map((p, i) => (
            <React.Fragment key={p.id || i}>
              <Box sx={{
                borderRadius: 2,
                p: 2,
                // backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                height: '100%'
              }}>
                {/* Close button for photo */}
                <Box display="flex" justifyContent="flex-end" mb={0.5}>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteEntry(p)}
                    sx={{
                      padding: 0.5,
                      "&:hover": {
                        backgroundColor: `${COLORS.ERROR}15`,
                      }
                    }}
                  >
                    <Icon name="close2" size={16} color={COLORS.ERROR} />
                  </IconButton>
                </Box>

                {/* Image */}
                <Box
                  component="img"
                  src={p.src}
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found";
                  }}
                  sx={{
                    width: "100%",
                    height: 200,
                    objectFit: "contain",
                    objectPosition: "center",
                    display: "block",
                    mb: 1.5,
                    borderRadius: 1,
                    backgroundColor: "#f5f5f5"
                  }}
                  alt={`Photo ${i + 1}`}
                />

                {/* Details */}
                <Box sx={{ flex: 1 }}>
                  {/* Activity/Location with proper text wrapping */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.TEXT,
                      fontWeight: 600,
                      mb: 0.3
                    }}>
                      Activity/Location:
                    </Typography>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.SECONDARY,
                      wordWrap: "break-word",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.4
                    }}>
                      {p.activity || p.photographDetails?.activityLocation || "-"}
                    </Typography>
                  </Box>

                  {/* Remark with proper text wrapping */}
                  <Box sx={{ mb: 1.5 }}>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.TEXT,
                      fontWeight: 600,
                      mb: 0.3
                    }}>
                      Remark:
                    </Typography>
                    <Typography sx={{
                      ...TYPOGRAPHY.BODY_SMALL,
                      color: COLORS.SECONDARY,
                      wordWrap: "break-word",
                      wordBreak: "break-word",
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      display: "-webkit-box",
                      WebkitLineClamp: 3,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      lineHeight: 1.4
                    }}>
                      {p.remark || p.photographDetails?.remarks || "-"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* EDIT FORM - Shows in a full-width row below */}
              {editingId === p.id && (
                <Box sx={{
                  gridColumn: "1 / -1",
                  mt: 2,
                  mb: 2,
                  p: 2,
                  borderRadius: 2,
                  backgroundColor: '#fff'
                }}>
                  <PhotoUploadWithDrawingDPR
                    open={true}
                    maxPhotos={1}
                    initialData={{
                      id: p.id,
                      activityLocation: p.activity || p.photographDetails?.activityLocation || "",
                      remarks: p.remark || p.photographDetails?.remarks || "",
                      imageUrl: p.src ? (p.src.startsWith('http') ? p.src : `${url}${p.src}`) : null,
                      shapes: p.photographDetails?.shapes || []
                    }}
                    onSave={(photoData) => {
                      console.log('AddDPR.js - onSave callback called with photoData:', photoData);
                      console.log('AddDPR.js - calling handleUpdatePhoto with id:', p.id);
                      handleUpdatePhoto(p.id, photoData);
                    }}
                    onClose={() => setEditingId(null)}
                  />
                </Box>
              )}
            </React.Fragment>
          ))}
        </Box>
      )}

      {/* ADD FORM */}
      {showAddForm && !editingId && (
        <Box mt={2} p={2} sx={{ border: `1px solid ${COLORS.LIGHT_SECONDARY}`, borderRadius: 2, backgroundColor: '#fff' }}>
          <PhotoUploadWithDrawingDPR
            open={true}
            maxPhotos={1}
            persistenceKey={`dpr-add-photo-${projectId}-${format(dprDate, "yyyy-MM-dd")}-new`}
            onClose={() => setShowAddForm(false)}
            onSave={(photoData) => {
              handleSaveNewPhoto(photoData);
            }}
          />
        </Box>
      )}

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </Box>
  );
}

function AddPhotoForm({ onCancel, projectId, dprDate, onAddEntry, refreshData, entry, onSuccess, showToast }) {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const isEditMode = !!entry;

  // Form state - Proper initialization
  const [formData, setFormData, clearFormData] = useFormPersistence(
    `dpr-add-photo-${projectId}-${format(dprDate, "yyyy-MM-dd")}-${isEditMode ? entry.id : 'new'}`,
    {
      activityLocation: entry?.activity || entry?.photographDetails?.activityLocation || "",
      remarks: entry?.remark || entry?.adminRemarks || entry?.photographDetails?.remarks || "",
    }
  );

  // Image state
  const [photoFile, setPhotoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(
    entry?.photographDetails?.photoPath
      ? `${url}${entry.photographDetails.photoPath}`
      : (entry?.src && !entry.src.startsWith('https://via.placeholder.com'))
        ? entry.src
        : null
  );

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file type only (remove size restriction)
      if (!file.type.startsWith('image/')) {
        showToast({
          type: "error",
          title: "Error",
          message: "Please select an image file (JPEG, PNG, etc.)"
        });
        return;
      }

      setPhotoFile(file);
      // Create a preview URL for the image
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      // Validation
      if (!photoFile && !isEditMode && !previewUrl) {
        setError("Please select a photo to upload");
        return;
      }

      const formDataToSend = new FormData();

      // 1. Append photo file - Use 'photos' field name (from your backend code)
      if (photoFile) {
        formDataToSend.append('photos', photoFile);
      }

      // 2. Required fields based on backend validation
      formDataToSend.append('projectId', projectId);
      formDataToSend.append('reportType', 'Photograph');
      formDataToSend.append('title', `Photograph - ${formData.activityLocation || 'Daily Report'}`);

      // 3. The CRITICAL part - send photographDetails as JSON string
      // This is what your backend validation is checking for
      const photographDetails = {
        activityLocation: formData.activityLocation || "",
        remarks: formData.remarks || "",
        shapes: [] // Will be populated by PhotoUploadWithDrawing
      };

      // Convert to JSON string and append
      formDataToSend.append('photographDetails', JSON.stringify(photographDetails));

      // 4. Optional fields (send them anyway)
      formDataToSend.append('adminRemarks', formData.remarks || '');

      // 5. Date field
      const formattedDate = new Date(dprDate);
      if (!isNaN(formattedDate.getTime())) {
        formDataToSend.append('reportDate', format(dprDate, "yyyy-MM-dd"));
      } else {
        formDataToSend.append('reportDate', format(new Date(), "yyyy-MM-dd"));
      }

      // 6. Debug: Check what's being sent
      console.log("=== FormData Contents ===");
      for (let [key, value] of formDataToSend.entries()) {
        console.log(`${key}:`, value instanceof File ? `File: ${value.name}` : value);
      }

      const config = {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      };

      // Determine endpoint based on edit mode
      let endpoint;
      let method;

      if (isEditMode && entry?.id && !entry.id.startsWith('new-')) {
        endpoint = `${url}/api/dpr/update-entry/${entry.id}`;
        method = 'put';
      } else {
        endpoint = `${url}/api/dpr/add`;
        method = 'post';
      }

      let response;
      if (method === 'put') {
        response = await axios.put(endpoint, formDataToSend, config);
      } else {
        response = await axios.post(endpoint, formDataToSend, config);
      }

      // Handle response
      if (response && (response.status === 200 || response.status === 201)) {
        const responseData = response.data.dpr || response.data.data || response.data;

        // Prepare the entry object for frontend
        let photoUrl = previewUrl;
        if (responseData.photographDetails?.photoPath) {
          const photoPath = responseData.photographDetails.photoPath;
          if (photoPath.startsWith('/')) {
            photoUrl = `${url}${photoPath}`;
          } else if (photoPath.includes('uploads')) {
            photoUrl = `${url}/${photoPath}`;
          } else {
            photoUrl = `${url}/uploads/${photoPath}`;
          }
        }

        const newEntry = {
          id: responseData._id || entry?.id,
          activity: responseData.photographDetails?.activityLocation || formData.activityLocation,
          remark: responseData.adminRemarks || responseData.photographDetails?.remarks || formData.remarks,
          src: photoUrl,
          photographDetails: responseData.photographDetails || photographDetails
        };

        // Update state based on mode
        if (isEditMode && onSuccess) {
          onSuccess(newEntry);
        } else if (!isEditMode && onAddEntry) {
          onAddEntry(newEntry);
        }

        // Show success toast
        if (showToast) {
          showToast({
            type: "success",
            title: "Success",
            message: isEditMode ? "Photograph updated successfully!" : "Photograph added successfully!"
          });
        }

        // Clear persistence on success
        clearFormData();

        // Refresh and close form
        setTimeout(() => {
          if (refreshData) refreshData();
          if (onCancel) onCancel();
        }, 1500);

      } else {
        throw new Error("Failed to save photograph");
      }

    } catch (err) {
      console.error("Error in handleSubmit:", err);

      // Extract error message
      let errorMsg = err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        "Failed to save photograph";

      setError(errorMsg);

      if (showToast) {
        showToast({
          type: "error",
          title: "Error",
          message: errorMsg
        });
      }
    } finally {
      setLoading(false);
    }
  };

  const inputSx = {
    "& .MuiOutlinedInput-root": {
      height: 44,
      borderRadius: "7px",
      "& fieldset": {
        borderColor: COLORS.LIGHT_SECONDARY,
      },
      "&:hover fieldset": {
        borderColor: COLORS.SECONDARY,
      },
      "&.Mui-focused fieldset": {
        borderColor: COLORS.SECONDARY,
      },
    },
    "& input, & textarea": {
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.PRIMARY,
      padding: "10px 12px",
    },
    "& input::placeholder, & textarea::placeholder": {
      color: COLORS.LIGHT_SECONDARY,
      opacity: 1,
    },
  };

  const Label = ({ children }) => (
    <Typography sx={{
      ...TYPOGRAPHY.BODY_LARGE,
      color: COLORS.SECONDARY,
      mb: 0.5,
      display: "block"
    }}>
      {children}
    </Typography>
  );

  return (
    <Box>
      <Typography sx={{ ...TYPOGRAPHY.H4, color: COLORS.SECONDARY, mb: 1.5 }}>
        {isEditMode ? "Edit Photograph Details:" : "Add New Photograph:"}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {isEditMode ? "Entry updated successfully!" : "Entry added successfully!"}
        </Alert>
      )}

      {/* IMAGE UPLOAD */}
      <Box mb={3}>
        <Label>{isEditMode ? "Current Photo" : "Upload Photo"}</Label>

        {previewUrl || entry?.photographDetails?.photoPath ? (
          <Box sx={{ position: "relative", width: "100%", maxWidth: 400, mb: 2 }}>
            <Box
              component="img"
              src={previewUrl || `${url}${entry.photographDetails.photoPath}`}
              sx={{
                width: "100%",
                height: 250,
                objectFit: "contain",
                borderRadius: "8px",
                border: `1px solid ${COLORS.BORDER}`,
              }}
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = "https://via.placeholder.com/300x200?text=Image+Not+Found";
              }}
            />
            <IconButton
              size="small"
              onClick={() => {
                setPreviewUrl(null);
                setPhotoFile(null);
              }}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                backgroundColor: "#fff",
                "&:hover": {
                  backgroundColor: "rgba(255, 255, 255, 0.9)",
                }
              }}
            >
              <Icon name="close2" color={COLORS.ERROR} />
            </IconButton>
          </Box>
        ) : (
          <Button
            component="label"
            sx={{
              border: `1px dashed ${COLORS.BORDER}`,
              borderRadius: "10px",
              height: 160,
              width: "100%",
              maxWidth: 400,
              textTransform: "none",
              color: COLORS.SECONDARY,
              "&:hover": {
                borderColor: COLORS.SECONDARY,
                backgroundColor: "rgba(25, 118, 210, 0.04)",
              }
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <Icon name="upload" size={24} sx={{ mb: 1 }} />
              <Typography sx={{ ...TYPOGRAPHY.BODY_LARGE }}>
                Click to upload photo
              </Typography>
              <Typography sx={{ ...TYPOGRAPHY.BODY_SMALL, color: COLORS.TEXT_SECONDARY, mt: 0.5 }}>
                JPEG, PNG (Any size)
              </Typography>
            </Box>
            <input
              hidden
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </Button>
        )}
      </Box>

      {/* FIELDS */}
      <Box display="flex" gap={3} mb={3}>
        {/* Activity/Location Field */}
        <Box flex={1.5}>
          <Label>Activity / Location</Label>
          <TextField
            fullWidth
            value={formData.activityLocation}
            onChange={(e) => setFormData({ ...formData, activityLocation: e.target.value })}
            sx={inputSx}
            placeholder="Enter activity/location"
          />
        </Box>

        {/* Remark Field - Same height as activity field */}
        <Box flex={1.5}>
          <Label>Remark</Label>
          <TextField
            fullWidth
            value={formData.remarks}
            onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
            sx={inputSx}
            placeholder="Enter remarks"
            // Make it single line like activity field
            InputProps={{
              style: {
                height: 44,
              }
            }}
          />
        </Box>
      </Box>

      {/* BUTTONS */}
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2}>
          <Button
            onClick={onCancel}
            startIcon={<Icon name="close2" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 100,
              borderRadius: "7px",
              px: 2,
              py: 1,
              border: `1px solid ${COLORS.ERROR}`,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
              }
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={() => {
              clearFormData();
              setFormData({
                activityLocation: entry?.activity || entry?.photographDetails?.activityLocation || "",
                remarks: entry?.remark || entry?.adminRemarks || entry?.photographDetails?.remarks || "",
              });
              setPreviewUrl(null);
              setPhotoFile(null);
              if (showToast) {
                showToast({
                  type: "info",
                  title: "Form Cleared",
                  message: "The form has been reset to its initial state."
                });
              }
            }}
            startIcon={<Icon name="delete" />}
            sx={{
              color: COLORS.ERROR,
              textTransform: "none",
              minWidth: 120,
              border: `1px solid ${COLORS.ERROR}`,
              borderRadius: "7px",
              px: 2,
              py: 1,
              "&:hover": {
                backgroundColor: `${COLORS.ERROR}15`,
                border: `1px solid ${COLORS.ERROR}`,
              }
            }}
          >
            Clear Fields
          </Button>
        </Box>
        <Button
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <Icon name={isEditMode ? "check" : "plus2"} />}
          onClick={handleSubmit}
          disabled={loading || success || (!photoFile && !isEditMode && !previewUrl)}
          sx={{
            backgroundColor: success ? COLORS.PRIMARY : COLORS.SECONDARY,
            color: "#fff",
            textTransform: "none",
            borderRadius: "7px",
            px: 3,
            height: 44,
            minWidth: 140,
            "&:hover": {
              backgroundColor: success ? COLORS.PRIMARY : COLORS.PRIMARY,
            },
            "&:disabled": {
              backgroundColor: COLORS.LIGHT_SECONDARY,
              color: COLORS.TEXT_SECONDARY,
            }
          }}
        >
          {loading ? (isEditMode ? "Saving..." : "Adding...") :
            success ? (isEditMode ? "Saved!" : "Added!") :
              (isEditMode ? "Update Photo" : "Add Photo")}
        </Button>
      </Box>
    </Box>
  );
}

/* ========================= SUBMIT DPR BUTTON ========================= */

function SubmitDPRButton({ projectId, dprDate, dprEntries }) {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, type: "success", title: "", message: "" });

  const showToast = (toastConfig) => {
    setToast({ ...toastConfig, open: true });
  };

  const handleCloseToast = () => {
    setToast({ ...toast, open: false });
  };

  const handleClearAllDrafts = () => {
    const dateStr = format(dprDate, "yyyy-MM-dd");
    const prefix = "dpr-";
    
    // We want to match: dpr-[section]-[projectId]-[dateStr]-[entryID/new]
    // The structure is fixed by useFormPersistence usage in AddDPR.js
    
    let clearedCount = 0;
    const keysToRemove = [];
    
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      // Key format: dpr-[section]-[projectId]-[date]-[entryID/new]
      if (key && key.startsWith(prefix) && key.includes(projectId) && key.includes(dateStr)) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
      clearedCount++;
    });

    if (clearedCount > 0) {
      showToast({
        type: "info",
        title: "Drafts Cleared",
        message: `Successfully cleared ${clearedCount} draft entries for this report.`
      });
      // Small delay then reload to ensure all form states are reset
      setTimeout(() => window.location.reload(), 1500);
    } else {
      showToast({
        type: "info",
        title: "No Drafts",
        message: "There are no saved drafts for this particular project and date."
      });
    }
  };

  const handleSubmitAllDPR = async () => {
    try {
      setLoading(true);
      setError(null);

      // We don't need to submit anything since entries are already saved individually
      // This is just to mark the DPR as complete
      await new Promise(resolve => setTimeout(resolve, 1000));

      setSuccess(true);
      setLoading(false);

      showToast({
        type: "success",
        title: "Success",
        message: "DPR submitted successfully! Engineers can now edit and fill in details."
      });
      setTimeout(() => navigate(-1), 1500);

    } catch (err) {
      console.error("Error submitting DPR:", err);
      const errorMsg = err.response?.data?.message || "Failed to submit DPR";
      setError(errorMsg);

      showToast({
        type: "error",
        title: "Error",
        message: errorMsg
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Box display="flex" justifyContent="center" alignItems="center" gap={3} mt={5} mb={5}>
        <Button
          onClick={handleClearAllDrafts}
          startIcon={<Icon name="delete" />}
          sx={{
            color: COLORS.ERROR,
            textTransform: "uppercase",
            border: `1px solid ${COLORS.ERROR}`,
            px: 4,
            py: 1.5,
            borderRadius: "8px",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: `${COLORS.ERROR}15`,
            }
          }}
        >
          Clear All Drafts
        </Button>

        <Button
          onClick={handleSubmitAllDPR}
          sx={{
            backgroundColor: COLORS.SECONDARY,
            color: "#fff",
            textTransform: "uppercase",
            px: 5,
            py: 1.5,
            borderRadius: "8px",
            fontWeight: 600,
            "&:hover": {
              backgroundColor: COLORS.PRIMARY,
            }
          }}
        >
          Submit Daily Progress Report
        </Button>
      </Box>

      {/* AppToast Component */}
      <AppToast
        open={toast.open}
        type={toast.type}
        title={toast.title}
        message={toast.message}
        onClose={handleCloseToast}
      />
    </>
  );
}

/* ========================= FLEX HELPERS ========================= */

function FlexRow({ children, bold, sx }) {
  return (
    <Box
      display="flex"
      alignItems="flex-start" // Standardized to top-alignment
      py={1}
      borderBottom={`1px solid ${COLORS.BORDER}`}
      sx={{ fontWeight: bold ? 600 : 400, ...sx }}
    >
      {children}
    </Box>
  );
}

function FlexCell({ children, flex, w, color, sx }) {
  return (
    <Box
      sx={{
        flex: flex || "none",
        width: w,
        fontSize: TYPOGRAPHY.BODY_STANDARD.fontSize,
        color: color ? COLORS.SECONDARY : COLORS.TEXT,
        paddingLeft: sx?.pl || 0,
        wordWrap: "break-word",
        whiteSpace: "normal",
        overflowWrap: "break-word",
        lineHeight: 1.4,
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
